import { useState, useMemo, useEffect, useRef, type Dispatch, type SetStateAction, type ReactNode } from "react"
import { useClientsSearch } from "@/hooks/use-clients-search"
import { useDriversSearch } from "@/hooks/use-drivers-search"
import { ClientAvatar } from "@/components/client-avatar"
import { InternationalShipmentIdsEditor } from "@/components/international-shipment-ids-editor"
import { Link, useNavigate } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { SiteHeader } from "@/components/site-header"
import { api, authorizedFetch } from "@/lib/api"
import { getAuth } from "@/lib/auth"
import { API_URL } from "@/lib/mutation-utils"
import { buildShipmentPayload as buildPayload } from "@/lib/shipment-payload"
import { usePermissions } from "@/hooks/use-permissions"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { SearchableSelect } from "@/components/ui/searchable-select"
import { Pagination } from "@/components/ui/pagination"
import { Avatar, AvatarFallback, AvatarImage, AvatarGroup, AvatarGroupCount } from "@/components/ui/avatar"
import {
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from "@/components/ui/tooltip"
import {
  SearchIcon,
  PlusIcon,
  PencilIcon,
  EyeIcon,
  Trash2Icon,
  RotateCcwIcon as RestoreIcon,
  ArrowRightIcon,
  LayoutListIcon,
  KanbanSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronsUpDownIcon,
  ChevronRightIcon,
  TriangleAlertIcon,
  CopyIcon,
  CalendarIcon,
  XIcon,
  PackageIcon,
  FileTextIcon,
  CalculatorIcon,
  DownloadIcon,
  RouteIcon,

  TruckIcon,
  MoreVerticalIcon,
  BanIcon,
  CircleCheckIcon,
  ReceiptIcon,
  MessageSquareIcon,
} from "lucide-react"
import { cn } from "@/lib/utils"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { avatarInitials, avatarColor } from "@/lib/avatar"
import { parseCSVRow, escapeCSVCell, escapeHTML } from "@/lib/csv"
import { BATEA_LOADING_WINDOWS, hasWorkLineAccess, shipmentAlertLevel, nextInternationalId, validateInternationalId } from "@cdc-sccr/shared"
import { useMobileEditor, MobileEditorPanel, MobileEditorConfirmDialog } from "@/hooks/use-mobile-editor"
import { usePersistedState } from "@/lib/use-persisted-state"
import ShipmentsKanban from "./ShipmentsKanban"
import { DELIVERY_NOTE_STATUS_LABEL, DELIVERY_NOTE_STATUS_CLASS, DELIVERY_NOTE_STATUS_HOVER_CLASS, DELIVERY_NOTE_STATUS_NEXT, formatRoutePath, type DeliveryNoteStatus } from "./ShipmentDetailUtils"
import { type TripRow } from "./ShipmentDetailTypes"
import type { TravelAdvance } from "@/lib/travel-advance"
import { ShipmentTripsTable } from "./ShipmentTripsTable"
import { QuickEditDialog, type QuickEditTarget } from "./QuickEditDialog"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card"
import { NationalRouteEditor } from "./NationalRouteEditor"
import { ShipmentRouteInfoBlock } from "@/components/shipment-route-info-block"
import { syncNationalRouteStops } from "@/lib/national-route"

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [25, 50, 100]

// ─── Types ────────────────────────────────────────────────────────────────────

type ShipmentStatus =
  | "en_planificacion"
  | "planificado"
  | "activo"
  | "en_curso"
  | "finalizado"
  | "cancelado"

type WorkLine = "nacional" | "internacional" | "batea"

interface ShipmentRow {
  id:                            string
  internalId:                    number
  internationalIds?:             string[]
  status:                        ShipmentStatus
  workLine:                      WorkLine | null
  externalId:                    string | null
  cargoValue:                    string | null
  notes:                         string | null
  scheduledOriginArrivalAt:      string | null
  scheduledDestinationArrivalAt: string | null
  deliveryNoteNumber:            string | null
  loadingWeightKg:               string | null
  unloadingWeightKg:             string | null
  deliveryNoteStatus:            DeliveryNoteStatus
  deletedAt:                     string | null
  createdAt:                     string
  updatedAt:                     string
  clientId:                      string | null
  clientName:                    string | null
  clientAvatarKey:               string | null
  clientAvatarUrl?:              string | null
  cargoTypeId:                   string | null
  cargoTypeName:                 string | null
  shipmentKm:                    number | null
  routeId?:                      string | null
  routeCode?:                    string | null
  routeName?:                    string | null
  routeKm?:                      number | null
  routeOriginCityName?:          string | null
  routeTargetCityId?:             string | null
  routeTargetCityName?:           string | null
  routeTargetStateName?:          string | null
  intermediateStopStateNames?:    string | null
  cargoRouteCode?:                string | null
  cargoRouteName?:                string | null
  firstProductId:                string | null
  productNames:                  string | null
  firstTripId:                   string | null
  firstTripDriverId:             string | null
  firstTripTruckPlate:           string | null
  firstTripTrailerPlate:         string | null
  firstTripHasTravelAdvance:     "viaje" | "gnc" | "viaje_gnc" | null
  firstTripMobileId:             string | null
  firstTripMobileNumber:         number | null
  tripCount:                     number
  operatorNames:                 string | null
  driverNames:                   string | null
  firstDriverPhone:              string | null
  // Sender party
  senderId:                      string | null
  senderName:                    string | null
  senderCityName:                string | null

  senderCountryName:             string | null
  // Recipient party
  recipientId:                   string | null
  recipientName:                 string | null
  recipientCityName:             string | null

  recipientCountryName:          string | null
  plannedAt:                     string | null
  originArrivalAt:               string | null
  originLoadingAt:               string | null
  destinationArrivalAt:          string | null
  destinationUnloadingAt:        string | null
  finalizedAt:                   string | null
  // Batea-specific
  loadingWindow:                 string | null
  finalDestinationId:            string | null
  cancelReason:                  string | null
  cancelDescription:             string | null
  billingStatus:                 "controlado" | "liquidado" | "facturado" | "cerrado" | null
  isRouted:                      boolean
  billingTags:                   string | null
}

interface FinalDestinationOption {
  id:   string
  name: string
  code: string
}

interface RouteOption {
  id: string
  code: string
  name: string
  km: number | null
  originCityId: string
  originCityName: string
  targetCityId: string
  targetCityName: string
  deletedAt?: string | null
}

interface ShipmentRouteStop {
  partyId: string
  kind: "origin" | "intermediate" | "destination"
}

interface ShipmentRouteStopRow extends ShipmentRouteStop {
  id: string
  sequence: number
  partyName: string
  cityId: string | null
  cityName: string | null
}

interface ShipmentRouteDetails {
  id: string
  code: string
  name: string
  km: number | null
  originCityName: string
  targetCityName: string
}

interface ShipmentStopsHoverResponse {
  stops: ShipmentRouteStopRow[]
  route: ShipmentRouteDetails | null
}

interface PartyOption {
  id:          string
  name:        string
  externalId:  string | null
  cityId:      string | null
  cityName:    string | null
  countryName: string | null
}

interface ClientOption    { id: string; name: string; code: string; avatarUrl?: string | null }
interface CargoTypeOption { id: string; name: string; code: string }
interface MobileOption {
  id:                string
  isActive:          boolean
  driverId:          string | null
  truckId:           string | null
  trailerId:         string | null
  driverName:        string | null
  truckLicensePlate: string | null
  truckUnitNumber:   number | null
  operatorName:      string | null
}

type FilterStatus = "active" | "deleted" | "all"

// ─── Billing status config ───────────────────────────────────────────────────

import { type BillingStatus, BILLING_STATUS_LABELS, BILLING_STATUS_COLORS, ALL_BILLING_STATUSES } from "@/lib/billing-status"

// ─── Status config ────────────────────────────────────────────────────────────

const ALL_STATUSES: ShipmentStatus[] = [
  "en_planificacion",
  "planificado",
  "activo",
  "en_curso",
  "finalizado",
  "cancelado",
]

// Excluded from edit forms — cancellation requires reason + description via the dedicated action.
const EDITABLE_STATUSES: ShipmentStatus[] = [
  "en_planificacion", "planificado", "activo", "en_curso", "finalizado",
]

const STATUS_LABELS: Record<ShipmentStatus, string> = {
  en_planificacion: "En planificación",
  planificado:      "Planificado",
  activo:           "Activo",
  en_curso:         "En curso",
  finalizado:       "Finalizado",
  cancelado:        "Cancelado",
}

const STATUS_COLORS: Record<ShipmentStatus, string> = {
  en_planificacion: "bg-muted text-muted-foreground",
  planificado:      "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  activo:           "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  en_curso:         "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  finalizado:       "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  cancelado:        "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
}

// ─── WorkLine config ──────────────────────────────────────────────────────────

const WORK_LINES: WorkLine[] = ["nacional", "internacional", "batea"]

const WORK_LINE_LABELS: Record<WorkLine, string> = {
  nacional:      "Nacional",
  internacional: "Internacional",
  batea:         "Batea",
}

const WORK_LINE_COLORS: Record<WorkLine, string> = {
  nacional:      "bg-blue-100 text-blue-700",
  internacional: "bg-purple-100 text-purple-700",
  batea:         "bg-amber-100 text-amber-700",
}

// ─── Form state ───────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  clientId:                      "",
  productId:                     "",
  originalProductId:             "",
  workLine:                      "" as WorkLine | "",
  status:                        "en_planificacion" as ShipmentStatus,
  mobileId:                      "",
  senderId:                      "",
  recipientId:                   "",
  scheduledOriginArrivalAt:      "",
  scheduledDestinationArrivalAt: "",
  deliveryNoteNumber:            "",
  notes:                         "",
  externalId:                    "",
  cargoValue:                    "",
  // Batea-specific (optional, only sent when workLine === "batea")
  loadingWindow:       "",
  finalDestinationId: "",
  billingStatus:      null as "controlado" | "liquidado" | "facturado" | "cerrado" | null,
  isRouted:           false,
  billingTags:        [] as string[],
  internationalIds:   [] as string[],
  confirmInternationalIdsDeletion: false,
  routeId:            "",
  routeStops:         [] as ShipmentRouteStop[],
}

type FormState = typeof EMPTY_FORM

// ─── Countdown component ────────────────────────────────────────────────────────

function formatCountdown(iso: string): { label: string; urgent: boolean } {
  const diff = new Date(iso).getTime() - Date.now()
  const abs  = Math.abs(diff)
  const past = diff < 0
  const mins  = Math.floor(abs / 60_000)
  const hours = Math.floor(mins / 60)
  const days  = Math.floor(hours / 24)
  let label: string
  if (days > 0)        label = `${past ? "hace" : "en"} ${days}d ${hours % 24}h`
  else if (hours > 0)  label = `${past ? "hace" : "en"} ${hours}h ${mins % 60}m`
  else if (mins > 0)   label = `${past ? "hace" : "en"} ${mins}m`
  else                 label = past ? "ahora" : "< 1m"
  return { label, urgent: !past && diff < 2 * 3_600_000 }
}

// ─── AvatarList component ────────────────────────────────────────────────────────

function AvatarList({ names }: { names: string }) {
  const list = names.split("||").filter(Boolean)
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger render={<span className="inline-flex items-center gap-0.5 cursor-default" />}>
          {list.slice(0, 2).map(name => (
            <Avatar key={name} size="sm">
              <AvatarFallback style={{ backgroundColor: avatarColor(name), color: "white" }}>
                {avatarInitials(name)}
              </AvatarFallback>
            </Avatar>
          ))}
          {list.length > 2 && (
            <span className="text-[10px] text-muted-foreground ml-0.5">+{list.length - 2}</span>
          )}
        </TooltipTrigger>
        <TooltipContent>{list.join(", ")}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function DriverList({ names }: { names: string }) {
  const list = names.split("||").filter(Boolean)
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger render={<span className="inline-flex items-center gap-0.5 cursor-default" />}>
          {list.slice(0, 2).map(name => (
            <Avatar key={name} size="sm">
              <AvatarFallback style={{ backgroundColor: avatarColor(name), color: "white" }}>
                {avatarInitials(name)}
              </AvatarFallback>
            </Avatar>
          ))}
          {list.length > 2 && (
            <span className="text-[10px] text-muted-foreground ml-0.5">+{list.length - 2}</span>
          )}
        </TooltipTrigger>
        <TooltipContent>{list.join(", ")}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function Countdown({ iso }: { iso: string }) {
  const [tick, setTick] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  useEffect(() => {
    timerRef.current = setInterval(() => setTick(t => t + 1), 60_000)
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [])
  void tick
  const { label, urgent } = formatCountdown(iso)
  return (
    <span className={`text-[10px] font-medium tabular-nums ${
      urgent ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground/60"
    }`}>
      {label}
    </span>
  )
}

function ShipmentStopsHoverCard({ shipmentId, children }: { shipmentId: string; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const { data, isLoading } = useQuery<ShipmentStopsHoverResponse>({
    queryKey: ["shipment-route-stops", shipmentId, "hover"],
    enabled: open,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/shipments/${shipmentId}/stops?includeRoute=true`)
      return res.json() as Promise<ShipmentStopsHoverResponse>
    },
  })
  const stops = data?.stops ?? []
  const route = data?.route ?? null

  return (
    <HoverCard open={open} onOpenChange={setOpen}>
      <HoverCardTrigger
        delay={250}
        closeDelay={150}
        render={
          <button
            type="button"
            className="block w-full cursor-pointer rounded-md border-0 bg-transparent p-0 text-left"
            aria-label="Ver paradas de la carga"
          />
        }
      >
        {children}
      </HoverCardTrigger>
      <HoverCardContent align="end">
        <p className="mb-2 text-xs font-semibold">Paradas de la carga</p>
        {isLoading ? (
          <p className="text-xs text-muted-foreground">Cargando paradas...</p>
        ) : stops.length === 0 ? (
          <p className="text-xs text-muted-foreground">No hay paradas registradas.</p>
        ) : (
          <div className="space-y-1.5">
            {stops.map((stop, index) => (
              <div key={stop.id} className="flex items-start gap-2 text-xs">
                <span className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold",
                  stop.kind === "origin"
                    ? "bg-emerald-100 text-emerald-700"
                    : stop.kind === "destination"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-orange-100 text-orange-700",
                )}>
                  {stop.kind === "origin" ? "O" : stop.kind === "destination" ? "D" : index}
                </span>
                <span className="min-w-0">
                  <span className="block font-medium">{stop.partyName}</span>
                  {stop.cityName && <span className="block text-muted-foreground">{stop.cityName}</span>}
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 border-t pt-2">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Ruta utilizada</p>
          {route ? (
            <div className="space-y-0.5 text-xs">
              <p className="font-mono font-medium">{route.code}</p>
              <p>{route.originCityName} → {route.targetCityName}</p>
              <p className="text-muted-foreground">{route.km ?? "—"} km</p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No hay ruta registrada.</p>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—"
  const d   = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function isoToLocalDatetime(iso: string | null | undefined): string {
  if (!iso) return ""
  const d   = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}


// ─── ShipmentTripsPanel ──────────────────────────────────────────────────────

function ShipmentTripsPanel({ shipmentId }: { shipmentId: string }) {
  const qc = useQueryClient()
  const { data: rows = [], isLoading } = useQuery<TripRow[]>({
    queryKey: ["shipment-trips", shipmentId],
    queryFn: async () => {
      const res = await authorizedFetch(
        `${API_URL}/api/trips?shipmentId=${shipmentId}&deleted=false&pageSize=500`,
      )
      const json = await res.json() as { data: TripRow[] }
      return json.data
    },
    staleTime: 0,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ["shipment-trips", shipmentId] })

  const toggleAdvanceMut = useMutation({
    mutationFn: async ({ t, next }: { t: TripRow; next: TravelAdvance }) => {
      await authorizedFetch(`${API_URL}/api/trips/${t.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hasTravelAdvance: next }),
      })
    },
    onMutate: ({ t, next }) => {
      qc.setQueryData<TripRow[]>(
        ["shipment-trips", shipmentId],
        (old) => old?.map(r => r.id === t.id ? { ...r, hasTravelAdvance: next } : r),
      )
      return { previous: t.hasTravelAdvance }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shipments"] })
    },
    onError: (_err, { t }, ctx) => {
      qc.setQueryData<TripRow[]>(
        ["shipment-trips", shipmentId],
        (old) => old?.map(r => r.id === t.id ? { ...r, hasTravelAdvance: ctx?.previous ?? null } : r),
      )
      toast.error(_err.message)
    },
  })

  if (isLoading) {
    return <div className="py-3 px-4 text-sm text-muted-foreground">Cargando viajes...</div>
  }

  if (rows.length === 0) {
    return <div className="py-3 px-4 text-sm text-muted-foreground italic">Sin viajes registrados.</div>
  }

  return (
    <ShipmentTripsTable
      trips={rows}
      actions={{
        onToggleAdvance: (t, next) => toggleAdvanceMut.mutate({ t, next }),
      }}
    />
  )
}

// ─── BateaConfigSection ───────────────────────────────────────────────────────

function BateaConfigSection({
  form,
  setForm,
  finalDestinations,
}: {
  form:               FormState
  setForm:            Dispatch<SetStateAction<FormState>>
  finalDestinations:  FinalDestinationOption[]
}) {
  return (
    <div className="grid gap-4 rounded-lg border p-3">
      <p className="text-sm font-medium">Configuración Batea <span className="text-[10px] font-normal text-muted-foreground">(opcional)</span></p>

      <div className="grid gap-1.5">
        <Label>Ventana de carga</Label>
        <Select
          value={form.loadingWindow || "__none__"}
          onValueChange={(v) => setForm(f => ({ ...f, loadingWindow: !v || v === "__none__" ? "" : v }))}
        >
          <SelectTrigger>
            <SelectValue>
              {form.loadingWindow
                ? (BATEA_LOADING_WINDOWS.find(w => w.slug === form.loadingWindow)?.label ?? form.loadingWindow)
                : <span className="text-muted-foreground">Sin asignar</span>}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Sin asignar</SelectItem>
            {BATEA_LOADING_WINDOWS.map(w => (
              <SelectItem key={w.slug} value={w.slug}>{w.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-1.5">
        <Label>Destino final</Label>
        <Select
          value={form.finalDestinationId || "__none__"}
          onValueChange={(v) => setForm(f => ({ ...f, finalDestinationId: !v || v === "__none__" ? "" : v }))}
        >
          <SelectTrigger>
            <SelectValue>
              {form.finalDestinationId
                ? (finalDestinations.find(d => d.id === form.finalDestinationId)?.name ?? form.finalDestinationId)
                : <span className="text-muted-foreground">Sin asignar</span>}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Sin asignar</SelectItem>
            {finalDestinations.map(d => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ShipmentsPage() {
  const { can } = usePermissions()
  const authUser = getAuth()?.user
  const canExportInternationalClientReport = authUser
    ? hasWorkLineAccess(authUser, "internacional")
    : false
  const qc       = useQueryClient()
  const navigate = useNavigate()

  // ── State ──────────────────────────────────────────────────────────────────
  const [search,          setSearch]          = useState("")
  const [page,            setPage]            = useState(1)
  const [filterStatuses,  setFilterStatuses]  = usePersistedState<ShipmentStatus[]>("shipments:filterStatuses", [...ALL_STATUSES])
  const [filterStatus,    setFilterStatus]    = usePersistedState<FilterStatus>("shipments:filterStatus", "active")
  const [sheetOpen,       setSheetOpen]       = useState(false)
  const [editing,         setEditing]         = useState<ShipmentRow | null>(null)
  const [form,            setForm]            = useState<FormState>({ ...EMPTY_FORM })
  const [pendingMobileSave, setPendingMobileSave] = useState<FormState | null>(null)
  const [formError,       setFormError]       = useState<string | null>(null)
  const [deleteOpen,      setDeleteOpen]      = useState(false)
  const [deleting,        setDeleting]        = useState<ShipmentRow | null>(null)
  const [restoringId,       setRestoringId]       = useState<string | null>(null)

  // Cancel dialog state
  const [cancelTarget,      setCancelTarget]      = useState<ShipmentRow | null>(null)
  const [cancelReason,      setCancelReason]      = useState<string>("")
  const [cancelDescription, setCancelDescription] = useState<string>("")
  const [cancelError,       setCancelError]       = useState<string>("")

  // Duplicate dialog state
  const [duplicateTarget,  setDuplicateTarget]  = useState<ShipmentRow | null>(null)
  const [duplicateCount,   setDuplicateCount]   = useState(1)
  const [duplicateOpen,    setDuplicateOpen]    = useState(false)

  // Copies field in create form (batea only)
  const [createCopies,     setCreateCopies]     = useState(1)

  const [viewMode,            setViewMode]            = usePersistedState<"table" | "kanban">("shipments:viewMode", "table")
  const [filterOnlyAlerts,    setFilterOnlyAlerts]    = usePersistedState<boolean>("shipments:filterOnlyAlerts", false)
  const [filterRouted,         setFilterRouted]         = usePersistedState<"all" | "routed" | "unrouted">("shipments:filterRouted", "all")
  const [changingStatusId,    setChangingStatusId]    = useState<string | null>(null)
  const [filterWorkLines,     setFilterWorkLines]     = usePersistedState<WorkLine[]>("shipments:filterWorkLines", [])
  const [filterOperatorNames, setFilterOperatorNames] = usePersistedState<string[]>("shipments:filterOperatorNames", [])
  const [filterClientId,      setFilterClientId]      = usePersistedState<string>("shipments:filterClientId", "")
  const [filterNoteStatus,    setFilterNoteStatus]    = usePersistedState<DeliveryNoteStatus | "">("shipments:filterNoteStatus", "")
  const [filterBilling,       setFilterBilling]       = usePersistedState<BillingStatus | "all">("shipments:filterBilling", "all")
  const [filterDriver,        setFilterDriver]        = usePersistedState<string>("shipments:filterDriver", "")
  const [sortField,           setSortField]           = useState<"internalId" | "scheduledOriginArrivalAt" | "unloadingWeightKg">("scheduledOriginArrivalAt")
  const [sortDir,             setSortDir]             = useState<"asc" | "desc">("desc")

  const nextInternationalIdQuery = useQuery<{ corridor: string | null; next: string | null; seed: string | null; error?: string }>({
    queryKey: ["shipment-international-id-next", form.senderId, form.recipientId],
    enabled: form.workLine === "internacional" && Boolean(form.senderId && form.recipientId),
    queryFn: async () => {
      const params = new URLSearchParams({ senderId: form.senderId, recipientId: form.recipientId })
      const res = await authorizedFetch(`${API_URL}/api/shipments/international-ids/next?${params}`)
      return res.json() as Promise<{ corridor: string | null; next: string | null; seed: string | null; error?: string }>
    },
    staleTime: 0,
  })


  function toggleSort(field: "internalId" | "scheduledOriginArrivalAt" | "unloadingWeightKg") {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDir("desc")
    }
  }
  const [pageSize,            setPageSize]            = usePersistedState<number>("shipments:pageSize", 25)
  const [originDateFrom,      setOriginDateFrom]      = usePersistedState("shipments:originDateFrom", "")
  const [originDateTo,        setOriginDateTo]        = usePersistedState("shipments:originDateTo", "")
  const [selectedIds,         setSelectedIds]         = useState<Set<string>>(new Set())
  const [shipmentExportPreview, setShipmentExportPreview] = useState<{ csvText: string; filename: string } | null>(null)
  const [bulkStatusPending,     setBulkStatusPending]     = useState(false)
  const [expandedIds,           setExpandedIds]           = useState<Set<string>>(new Set())

  // Confirmation dialog for status changes (bulk or inline)
  const [quickEditTarget, setQuickEditTarget] = useState<QuickEditTarget | null>(null)

  function openQuickEdit(type: "mobile" | "sender" | "recipient" | "loadingWindow" | "notes", d: ShipmentRow) {
    setQuickEditTarget({
      type,
      shipmentId:           d.id,
      internalId:           d.internalId,
      firstTripId:          d.firstTripId,
      currentMobileId:      d.firstTripMobileId,
      currentSenderId:      d.senderId,
      currentRecipientId:   d.recipientId,
      currentLoadingWindow: d.loadingWindow,
      currentNotes:         d.notes,
    })
  }

  const [confirmDialog, setConfirmDialog] = useState<{
    title: string
    description: string
    onConfirm: () => void
  } | null>(null)

  function toggleExpand(id: string) {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Reset page on any filter change
  useEffect(() => { setPage(1) }, [search, filterStatuses, filterStatus, filterWorkLines, filterOperatorNames, filterClientId, filterNoteStatus, originDateFrom, originDateTo, filterBilling, filterDriver, sortField, sortDir, pageSize, viewMode])

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: appConfig } = useQuery({
    queryKey: ["app-config"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/app-config`)
      return res.json() as Promise<{
        trips:     { basePartyId: string; averageSpeedKmh: number; baseCityId: string | null; baseCityName: string | null }
        shipments: { originStaleYellowHours: number; originStaleRedHours: number; destinationStaleYellowHours: number; destinationStaleRedHours: number }
      }>
    },
    staleTime: 5 * 60 * 1_000,
  })

  const { data: shipmentsResponse, isLoading } = useQuery({
    queryKey:        ["shipments", filterStatus, viewMode, page, pageSize, originDateFrom, originDateTo,
                     search, filterStatuses, filterWorkLines, filterClientId, filterNoteStatus,
                     filterBilling, filterRouted, filterDriver, filterOperatorNames, sortField, sortDir],
    refetchInterval:      30_000,
    refetchOnWindowFocus: true,
    queryFn:  async () => {
      const params = new URLSearchParams()
      if (filterStatus === "active")  params.set("deleted", "false")
      if (filterStatus === "deleted") params.set("deleted", "true")
      if (viewMode === "kanban")      params.set("excludeFinalizado", "true")
      if (originDateFrom)             params.set("dateFrom", originDateFrom)
      if (originDateTo)               params.set("dateTo",   originDateTo)
      if (search.trim())              params.set("search", search.trim())
      const effectiveStatuses = viewMode === "kanban"
        ? filterStatuses.filter(s => s !== "finalizado")
        : filterStatuses
      if (effectiveStatuses.length > 0 && effectiveStatuses.length < ALL_STATUSES.length)
                                      params.set("statuses", effectiveStatuses.join(","))
      if (filterWorkLines.length > 0) params.set("workLines", filterWorkLines.join(","))
      if (filterClientId)             params.set("clientId", filterClientId)
      if (filterNoteStatus)           params.set("noteStatus", filterNoteStatus)
      if (filterBilling !== "all")    params.set("billingStatus", filterBilling)
      if (viewMode === "kanban" && filterRouted !== "all")
                                      params.set("isRouted", filterRouted === "routed" ? "true" : "false")
      if (filterDriver)               params.set("driverName", filterDriver)
      if (filterOperatorNames.length > 0)
                                      params.set("operatorNames", filterOperatorNames.join(","))
      params.set("sortField", sortField)
      params.set("sortDir",   sortDir)
      // Kanban needs all records — no pagination
      if (viewMode === "kanban") {
        params.set("page",     "1")
        params.set("pageSize", "500")
      } else {
        params.set("page",     String(page))
        params.set("pageSize", String(pageSize))
      }
      const res = await authorizedFetch(
        `${API_URL}/api/shipments?${params}`,
      )
      return res.json() as Promise<{ data: ShipmentRow[]; total: number }>
    },
  })
  const shipmentsData = shipmentsResponse?.data ?? []
  const serverTotal   = shipmentsResponse?.total ?? 0

  const { data: finalDestinationOptions = [] } = useQuery<FinalDestinationOption[]>({
    queryKey: ["final-destinations-select"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/batea/final-destinations?deleted=false`)
      return res.ok ? (res.json() as Promise<FinalDestinationOption[]>) : []
    },
    staleTime: 60_000,
  })

  const {
    clients: clientOptions,
    onSearchChange: onClientSearchChange,
  } = useClientsSearch()

  const {
    drivers: driverFilterOptions,
    onSearchChange: onDriverSearchChange,
    inputValue: driverInputValue,
    isMinLength: driverIsMinLength,
  } = useDriversSearch()

  const editingClientId = editing?.clientId ?? null
  const { data: editingClientData } = useQuery<ClientOption | null>({
    queryKey: ["client-by-id", editingClientId],
    enabled:  !!editingClientId && sheetOpen,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/clients/${editingClientId}`)
      if (!res.ok) return null
      const json = await res.json() as { id: string; name: string; code: string; sgtCodes?: string[] | null }
      return { id: json.id, name: json.name, code: json.code, sgtCodes: json.sgtCodes ?? null }
    },
  })

  const { data: partyOptions = [] } = useQuery({
    queryKey: ["parties-select"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/parties?deleted=false&page=1&pageSize=500`)
      if (!res.ok) return []
      const json = await res.json() as { data: PartyOption[] }
      return json.data
    },
    staleTime: 60_000,
  })

  const { data: routeOptions = [] } = useQuery<RouteOption[]>({
    queryKey: ["geo", "routes", "shipments-select"],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/geo/routes`)
      if (!res.ok) return []
      return (await res.json() as RouteOption[]).filter(route => !route.deletedAt)
    },
    enabled: sheetOpen,
    staleTime: 60_000,
  })

  const { data: editingRouteStops = [] } = useQuery<ShipmentRouteStopRow[]>({
    queryKey: ["shipment-route-stops", editing?.id],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/shipments/${editing!.id}/stops`)
      return res.ok ? (res.json() as Promise<ShipmentRouteStopRow[]>) : []
    },
    enabled: Boolean(editing?.id && sheetOpen),
    staleTime: 60_000,
  })

  useEffect(() => {
    if (!editing || form.routeStops.length > 0 || editingRouteStops.length === 0) return
    setForm(previous => ({
      ...previous,
      routeStops: editingRouteStops.map(({ partyId, kind }) => ({ partyId, kind })),
    }))
  }, [editing, editingRouteStops, form.routeStops.length])

  const { data: cargoProductOptions = [] } = useQuery({
    queryKey: ["cargo-products-select"],
    queryFn:  async () => {
      const res = await api.cargos.products.$get({ query: { deleted: "false" } })
      if (!res.ok) return []
      return res.json() as Promise<CargoTypeOption[]>
    },
    staleTime: 60_000,
  })

  const { data: mobileOptions = [] } = useQuery({
    queryKey: ["mobiles-select"],
    enabled:  sheetOpen,
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/mobiles`)
      return res.ok ? (res.json() as Promise<MobileOption[]>) : []
    },
    staleTime: 60_000,
  })

  const { data: driverList = [] } = useQuery({
    queryKey: ["drivers-select"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/providers/drivers?deleted=false&pageSize=500`)
      if (!res.ok) return []
      const json = await res.json() as { data: { id: string; name: string; code: string }[] }
      return json.data
    },
    enabled:   sheetOpen,
    staleTime: 60_000,
  })

  const { data: unitList = [] } = useQuery({
    queryKey: ["units-select"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/units?deleted=false`)
      if (!res.ok) return []
      const body = await res.json() as { data: { id: string; licensePlate: string | null; unitNumber: number | null }[]; total: number }
      return body.data ?? []
    },
    enabled:   sheetOpen,
    staleTime: 60_000,
  })

  const { data: operatorList = [] } = useQuery({
    queryKey: ["operators-select"],
    queryFn:  async () => {
      const res = await authorizedFetch(`${API_URL}/api/users/operators`)
      if (!res.ok) return []
      return res.json() as Promise<{ id: string; name: string }[]>
    },
    staleTime: 5 * 60_000,
  })

  const uniqueOperatorNames = useMemo(
    () => operatorList.map(o => o.name).filter(Boolean).sort((a, b) => a.localeCompare(b)),
    [operatorList],
  )

  const totalPages = Math.max(1, Math.ceil(serverTotal / pageSize))

  const allSelected  = shipmentsData.length > 0 && shipmentsData.every(d => selectedIds.has(d.id))
  const someSelected = shipmentsData.some(d => selectedIds.has(d.id)) && !allSelected

  function toggleSelectAll() {
    setSelectedIds(prev => {
      const next = new Set(prev)
      allSelected
        ? shipmentsData.forEach(d => next.delete(d.id))
        : shipmentsData.forEach(d => next.add(d.id))
      return next
    })
  }

  function toggleSelect(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  // Items for kanban: server already filters by excludeFinalizado + all other filters.
  // Client strips deleted rows as a safety net in case of stale cache.
  // When filterOnlyAlerts is active, further restrict to cards with any alert level.
  const alertConfig = appConfig?.shipments
  const kanbanFiltered = useMemo(() => {
    // Server handles isRouted, workLine, billing, status and pagination filters.
    // Client strips deleted rows (safety net) and applies filterOnlyAlerts (live timer — can't be server-side).
    const base = shipmentsData.filter(d => !d.deletedAt && d.status !== "finalizado")
    if (!filterOnlyAlerts) return base
    return base.filter(d => shipmentAlertLevel(d, Date.now(), alertConfig) !== null)
  }, [shipmentsData, filterOnlyAlerts, alertConfig])

  // ── Select options ─────────────────────────────────────────────────────────

  const clientSelectOptions = useMemo(() => {
    const opts = clientOptions.map(c => ({ value: c.id, label: c.name, sublabel: c.code }))
    if (editingClientData && !opts.some(o => o.value === editingClientData.id)) {
      opts.unshift({ value: editingClientData.id, label: editingClientData.name, sublabel: editingClientData.code })
    }
    return [{ value: "__none__", label: "Sin asignar" }, ...opts]
  }, [clientOptions, editingClientData])

  const cargoProductSelectOptions = useMemo(() => [
    { value: "__none__", label: "Sin producto" },
    ...cargoProductOptions.map(p => ({ value: p.id, label: p.name, sublabel: p.code })),
  ], [cargoProductOptions])

  const senderSelectOptions = useMemo(() => [
    { value: "__none__", label: "Sin asignar" },
    ...partyOptions
      .map(p => ({
        value:    p.id,
        label:    p.name,
        sublabel: [p.externalId ? `ID: ${p.externalId}` : null, p.cityName, p.countryName].filter(Boolean).join(" / ") || undefined,
        keywords: p.externalId ?? undefined,
      })),
  ], [partyOptions, form.recipientId])

  const recipientSelectOptions = useMemo(() => [
    { value: "__none__", label: "Sin asignar" },
    ...partyOptions
      .map(p => ({
        value:    p.id,
        label:    p.name,
        sublabel: [p.externalId ? `ID: ${p.externalId}` : null, p.cityName, p.countryName].filter(Boolean).join(" / ") || undefined,
        keywords: p.externalId ?? undefined,
      })),
  ], [partyOptions, form.senderId])


  const selectedRoute = form.workLine === "nacional"
    ? routeOptions.find(route => route.id === form.routeId) ?? null
    : null
  const senderParty = partyOptions.find(party => party.id === form.senderId) ?? null
  const recipientParty = partyOptions.find(party => party.id === form.recipientId) ?? null
  const connectionRoute = senderParty?.cityId && recipientParty?.cityId
    ? routeOptions.find(route => route.originCityId === senderParty.cityId && route.targetCityId === recipientParty.cityId) ?? null
    : null
  const effectiveRoute = selectedRoute ?? connectionRoute
  const cargoRoute = selectedRoute && senderParty?.cityId
    ? routeOptions.find(route =>
        route.originCityId === senderParty.cityId &&
        route.targetCityId === selectedRoute.targetCityId
      ) ?? null
    : effectiveRoute

  const mobileEditor = useMobileEditor({
    mobileId:      form.mobileId,
    mobileOptions,
    driverList,
    unitList,
    onError: (msg) => setFormError(msg),
  })

  // ── Mutations ──────────────────────────────────────────────────────────────

  const invalidate = () => qc.invalidateQueries({ queryKey: ["shipments"] })

  const statusMut = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ShipmentStatus }) => {
      await authorizedFetch(`${API_URL}/api/shipments/${id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ status }),
      })
    },
    onMutate: async ({ id, status }) => {
      setChangingStatusId(id)
      // Cancel outgoing refetches to avoid overwriting optimistic update
      await qc.cancelQueries({ queryKey: ["shipments"] })
      // Snapshot current cache for rollback on error
      const queryKey = ["shipments", filterStatus] as const
      const previous = qc.getQueryData<ShipmentRow[]>(queryKey)
      // Move card immediately in the UI
      qc.setQueryData<ShipmentRow[]>(queryKey, old =>
        old?.map(r => r.id === id ? { ...r, status } : r) ?? []
      )
      return { previous, queryKey }
    },
    onError: (err: Error, _, context) => {
      // Revert on failure
      if (context?.previous) {
        qc.setQueryData(context.queryKey, context.previous)
      }
      toast.error(err.message)
    },
    onSuccess: () => toast.success("Estado actualizado"),
    onSettled: () => {
      invalidate()
      setChangingStatusId(null)
    },
  })

  async function createTripIfNeeded(shipmentId: string, f: FormState) {
    if (!f.mobileId) return
    const originCityId      = partyOptions.find(p => p.id === f.senderId)?.cityId ?? null
    const destinationCityId = partyOptions.find(p => p.id === f.recipientId)?.cityId ?? null
    const mobile = mobileOptions.find(m => m.id === f.mobileId)
    await authorizedFetch(`${API_URL}/api/trips`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({
        shipmentId,
        mobileId:   f.mobileId,
        driverId:   mobile?.driverId  ?? null,
        truckId:    mobile?.truckId   ?? null,
        trailerId:  mobile?.trailerId ?? null,
        originCityId,
        destinationCityId,
      }),
    })
  }

  const createMut = useMutation({
    mutationFn: async (f: FormState) => {
      const payload = buildPayload(f, false)
      const copies  = f.workLine === "batea" ? createCopies : 1

      if (copies > 1) {
        // Create N shipments in parallel for batea bulk planning
        const results = await Promise.all(
          Array.from({ length: copies }, () =>
            authorizedFetch(`${API_URL}/api/shipments`, {
              method:  "POST",
              headers: { "Content-Type": "application/json" },
              body:    JSON.stringify(payload),
            }).then(r => r.json())
          )
        )
        return results[0]
      }

      const res    = await authorizedFetch(`${API_URL}/api/shipments`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      })
      const result = await res.json()
      await createTripIfNeeded(result.id, f)
      return result
    },
    onSuccess: () => {
      invalidate()
      qc.invalidateQueries({ queryKey: ["trips"] })
      qc.invalidateQueries({ queryKey: ["shipment-international-id-next"] })
      const copies = form.workLine === "batea" ? createCopies : 1
      toast.success(copies > 1 ? `${copies} envíos creados exitosamente` : "Envío creado exitosamente")
      closeSheet()
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const duplicateMut = useMutation({
    mutationFn: async ({ id, count }: { id: string; count: number }) => {
      const res = await authorizedFetch(`${API_URL}/api/shipments/${id}/duplicate`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ count }),
      })
      return res.json() as Promise<{ ids: string[] }>
    },
    onSuccess: (_data, { count }) => {
      invalidate()
      toast.success(`${count} envío${count !== 1 ? "s" : ""} duplicado${count !== 1 ? "s" : ""} exitosamente`)
      setDuplicateOpen(false)
      setDuplicateTarget(null)
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const updateMut = useMutation({
    mutationFn: async (f: FormState) => {
      if (!editing) throw new Error("Sin envío seleccionado")
      const res    = await authorizedFetch(`${API_URL}/api/shipments/${editing.id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(buildPayload(f, true)),
      })
      const result = await res.json() as { error?: string }
      if (!res.ok) throw new Error(result.error ?? "No se pudo actualizar el envío")
      if (!editing.firstTripId) {
        // No trip yet — create one with the selected mobile
        await createTripIfNeeded(editing.id, f)
      } else if (editing.tripCount <= 1 && f.mobileId !== (editing.firstTripMobileId ?? "")) {
        // Only update the trip if there is exactly one trip and the mobile changed
        const mobile = mobileOptions.find(m => m.id === f.mobileId)
        await authorizedFetch(`${API_URL}/api/trips/${editing.firstTripId}`, {
          method:  "PATCH",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({
            mobileId:  f.mobileId  || null,
            driverId:  mobile?.driverId  ?? null,
            truckId:   mobile?.truckId   ?? null,
            trailerId: mobile?.trailerId ?? null,
          }),
        })
      }
      return result
    },
    onSuccess: () => {
      invalidate()
      if (editing?.id) {
        qc.invalidateQueries({ queryKey: ["shipment-route-stops", editing.id] })
      }
      qc.invalidateQueries({ queryKey: ["trips"] })
      toast.success("Envío actualizado exitosamente")
      closeSheet()
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      await authorizedFetch(`${API_URL}/api/shipments/${id}`, { method: "DELETE" })
    },
    onSuccess: () => {
      invalidate()
      toast.success("Envío eliminado exitosamente")
      setDeleteOpen(false)
      setDeleting(null)
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const restoreMut = useMutation({
    mutationFn: async (id: string) => {
      setRestoringId(id)
      try {
        await authorizedFetch(`${API_URL}/api/shipments/${id}/restore`, { method: "PATCH" })
      } finally {
        setRestoringId(null)
      }
    },
    onSuccess: () => {
      invalidate()
      toast.success("Envío restaurado exitosamente")
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const cancelMut = useMutation({
    mutationFn: async ({ id, reason, description }: { id: string; reason: string; description: string }) => {
      await authorizedFetch(`${API_URL}/api/shipments/${id}/cancel`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ reason, description }),
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shipments"] })
      setCancelTarget(null)
      setCancelReason("")
      setCancelDescription("")
      setCancelError("")
    },
    onError: (err: Error) => setCancelError(err.message),
  })

  const toggleRoutedMut = useMutation({
    mutationFn: async ({ id, isRouted }: { id: string; isRouted: boolean }) => {
      await authorizedFetch(`${API_URL}/api/shipments/${id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ isRouted }),
      })
    },
    onMutate: ({ id, isRouted }) => {
      const previous = qc.getQueriesData({ queryKey: ["shipments"], exact: false })
      qc.setQueriesData({ queryKey: ["shipments"], exact: false }, (old: unknown) => {
        if (!old || typeof old !== "object" || !("data" in old)) return old
        const data = old as { data: ShipmentRow[]; total: number }
        return { ...data, data: data.data.map(r => r.id === id ? { ...r, isRouted } : r) }
      })
      return { previous }
    },
    onSuccess: (_data, { isRouted }) => {
      toast.success(isRouted ? "Carga ruteada" : "Carga marcada como no ruteada")
    },
    onError: (err: Error, _vars, ctx) => {
      if (ctx?.previous) qc.setQueriesData({ queryKey: ["shipments"], exact: false }, ctx.previous)
      toast.error(err.message)
    },
  })

  const remitoStatusMut = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "normal" | "validado" | "reclamado" }) => {
      await authorizedFetch(`${API_URL}/api/shipments/${id}`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ deliveryNoteStatus: status }),
      })
    },
    onSuccess: () => invalidate(),
    onError:   (err: Error) => toast.error(err.message),
  })

  // ── Helpers ────────────────────────────────────────────────────────────────

  function closeSheet() {
    setSheetOpen(false)
    setEditing(null)
    setForm({ ...EMPTY_FORM })
    setPendingMobileSave(null)
    setFormError(null)
    setCreateCopies(1)
    mobileEditor.reset()
  }

  function openCreate() {
    qc.removeQueries({ queryKey: ["shipment-international-id-next"] })
    setEditing(null)
    setForm({ ...EMPTY_FORM })
    setPendingMobileSave(null)
    setFormError(null)
    setCreateCopies(1)
    mobileEditor.reset()
    setSheetOpen(true)
  }

  function openDuplicate(d: ShipmentRow) {
    setDuplicateTarget(d)
    setDuplicateCount(1)
    setDuplicateOpen(true)
  }

  function openEdit(d: ShipmentRow) {
    setEditing(d)
    setForm({
      clientId:                      d.clientId          ?? "",
      productId:                     d.firstProductId    ?? "",
      originalProductId:             d.firstProductId    ?? "",
      workLine:                      (d.workLine         ?? "") as WorkLine | "",
      status:                        d.status,
      mobileId:                      d.firstTripMobileId ?? "",
      senderId:                      d.senderId          ?? "",
      recipientId:                   d.recipientId       ?? "",
      scheduledOriginArrivalAt:      isoToLocalDatetime(d.scheduledOriginArrivalAt),
      scheduledDestinationArrivalAt: isoToLocalDatetime(d.scheduledDestinationArrivalAt),
      deliveryNoteNumber:            d.deliveryNoteNumber ?? "",
      notes:                         d.notes             ?? "",
      externalId:                    d.externalId        ?? "",
      cargoValue:                    d.cargoValue        ?? "",
      loadingWindow:       d.loadingWindow      ?? "",
      finalDestinationId: d.finalDestinationId ?? "",
      billingStatus:      d.billingStatus      ?? null,
      isRouted:           d.isRouted,
      billingTags:        d.billingTags ? d.billingTags.split("||").map(t => t.trim()).filter(Boolean) : [],
      internationalIds:   d.internationalIds ?? [],
      confirmInternationalIdsDeletion: false,
      routeId:            d.routeId ?? "",
      routeStops:         [],
    })
    setPendingMobileSave(null)
    setFormError(null)
    mobileEditor.reset()
    setSheetOpen(true)
  }


  function openDelete(d: ShipmentRow) {
    setDeleting(d)
    setDeleteOpen(true)
  }

  function openCancel(d: ShipmentRow) {
    setCancelTarget(d)
    setCancelReason("")
    setCancelDescription("")
    setCancelError("")
  }

  function setRouteEndpoint(field: "senderId" | "recipientId", value: string) {
    setForm(previous => {
      const next = { ...previous, [field]: value }
      if (previous.workLine === "nacional") {
        next.routeStops = syncNationalRouteStops(
          field === "senderId" ? value : previous.senderId,
          field === "recipientId" ? value : previous.recipientId,
          previous.routeStops,
        )
      }
      return next
    })
  }



  function toggleStatus(s: ShipmentStatus) {
    setFilterStatuses(prev =>
      prev.includes(s)
        ? prev.length === 1 ? prev               // keep at least one
          : prev.filter(x => x !== s)
        : [...prev, s],
    )
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError(null)

    if (!form.senderId && !form.clientId) {
      setFormError("Debe indicar al menos un remitente o cliente")
      return
    }
    if (form.workLine !== "nacional" && form.senderId && form.recipientId && form.senderId === form.recipientId) {
      setFormError("El remitente y el destinatario no pueden ser el mismo")
      return
    }
    if (form.workLine === "nacional" && form.routeStops.length > 0) {
      const hasIncompleteStop = form.routeStops.some(stop => !stop.partyId)
      if (hasIncompleteStop) {
        setFormError("Todas las paradas deben tener un party")
        return
      }
    }
    if (!form.workLine) {
      setFormError("Debe seleccionar una línea de trabajo")
      return
    }
    const invalidInternationalId = form.workLine === "internacional" && nextInternationalIdQuery.data?.seed
      ? form.internationalIds.find(value => !validateInternationalId(value, nextInternationalIdQuery.data!.seed!))
      : undefined
    if (invalidInternationalId) {
      setFormError(`El ID ${invalidInternationalId} no respeta el prefijo y formato del corredor ${nextInternationalIdQuery.data?.corridor ?? "seleccionado"}.`)
      return
    }

    const payloadInternationalIds = buildPayload(form, Boolean(editing)).internationalIds
    if (editing && editing.internationalIds && editing.internationalIds.some(value => !payloadInternationalIds.includes(value))) {
      confirmThen(
        "Eliminar IDs internacionales",
        "Se eliminarán IDs internacionales existentes de esta carga. Esta operación es destructiva y los valores no podrán reutilizarse. ¿Deseás continuar?",
        () => {
          if (mobileEditor.wasEdited) {
            setPendingMobileSave({ ...form, confirmInternationalIdsDeletion: true })
            mobileEditor.openConfirm()
          } else doSave({ ...form, confirmInternationalIdsDeletion: true })
        },
      )
      return
    }

    if (mobileEditor.wasEdited) {
      // Always overwrite: a previously cancelled confirm may have left a stale form here.
      setPendingMobileSave(form)
      mobileEditor.openConfirm()
    } else {
      doSave(form)
    }
  }

  function doSave(f: FormState) {
    if (editing) updateMut.mutate(f)
    else         createMut.mutate(f)
  }

  function confirmThen(title: string, description: string, onConfirm: () => void) {
    setConfirmDialog({ title, description, onConfirm })
  }

  async function patchShipmentStatus(id: string, payload: { status?: ShipmentStatus; billingStatus?: "controlado" | "liquidado" | "facturado" | "cerrado" | null }) {
    await authorizedFetch(`${API_URL}/api/shipments/${id}`, {
      method:  "PATCH",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify(payload),
    })
    qc.invalidateQueries({ queryKey: ["shipments"] })
    // Refresh expanded trip panels — billingStatus cascades to trips on the API
    if (payload.billingStatus !== undefined) {
      qc.invalidateQueries({ queryKey: ["shipment-trips", id] })
    }
  }

  async function handleBulkStatusUpdate(payload: {
    status?: ShipmentStatus
    billingStatus?: "controlado" | "liquidado" | "facturado" | "cerrado" | null
  }) {
    if (selectedIds.size === 0) return
    setBulkStatusPending(true)
    try {
      await authorizedFetch(`${API_URL}/api/shipments/bulk-status`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ ids: [...selectedIds], ...payload }),
      })
      const count = selectedIds.size
      qc.invalidateQueries({ queryKey: ["shipments"] })
      // Refresh all expanded trip panels — billingStatus cascades to trips on the API
      if (payload.billingStatus !== undefined) {
        qc.invalidateQueries({ queryKey: ["shipment-trips"] })
      }
      toast.success(`${count} carga${count !== 1 ? "s" : ""} actualizadas`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo actualizar el estado de las cargas")
    } finally {
      setBulkStatusPending(false)
    }
  }

  async function handleShipmentExport(endpoint: "cargo-data" | "cargo-documents" | "valuation" | "trip-data" | "international-client-report") {
    try {
      const res = await authorizedFetch(
        `${API_URL}/api/shipments/export/${endpoint}`,
        {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ ids: [...selectedIds] }),
        },
      )
      const disposition = res.headers.get("Content-Disposition") ?? ""
      const match       = disposition.match(/filename="([^"]+)"/)
      const filename    = match?.[1] ?? `${endpoint}.csv`
      const csvText     = await res.text()
      setShipmentExportPreview({ csvText, filename })
    } catch (err) {
      console.error("Export failed", err)
    }
  }

  function handleDownloadShipmentPreview(cleanCsvText: string) {
    if (!shipmentExportPreview) return
    const blob = new Blob([cleanCsvText], { type: "text/csv;charset=utf-8;" })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement("a")
    a.href     = url
    a.download = shipmentExportPreview.filename
    a.click()
    URL.revokeObjectURL(url)
  }

  function handleDownloadShipmentXlsPreview(headerRows: string[][], okRows: string[][]) {
    if (!shipmentExportPreview) return
    const esc = escapeHTML
    let html = `<!DOCTYPE html><html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"></head><body><table>`
    html += "<thead>" + headerRows.map(row => `<tr>${row.map(h => `<th>${esc(h)}</th>`).join("")}</tr>`).join("") + "</thead>"
    html += "<tbody>" + okRows.map(r => `<tr>${r.map(c => `<td>${esc(c)}</td>`).join("")}</tr>`).join("") + "</tbody>"
    html += "</table></body></html>"
    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement("a")
    a.href     = url
    a.download = shipmentExportPreview.filename.replace(/\.csv$/i, ".xls")
    a.click()
    URL.revokeObjectURL(url)
  }

  const isSaving = createMut.isPending || updateMut.isPending || mobileEditor.isPending
  const isDuplicating = duplicateMut.isPending

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <SiteHeader title="Cargas" />

      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6 space-y-4">

        {/* ── Toolbar ── */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Search */}
          <div className="relative w-72">
            <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar remitente, destinatario, cliente, código, remito..."
              className="pl-8"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            />
          </div>

          <div className="flex items-center gap-2">
            {/* View mode toggle */}
            <div className="flex items-center rounded-md border bg-muted p-0.5 gap-0.5">
              <Button
                variant={viewMode === "table" ? "default" : "ghost"}
                size="icon-sm"
                onClick={() => setViewMode("table")}
                title="Vista tabla"
                className={viewMode === "table" ? "" : "text-muted-foreground"}
              >
                <LayoutListIcon className="size-4" />
              </Button>
              <Button
                variant={viewMode === "kanban" ? "default" : "ghost"}
                size="icon-sm"
                onClick={() => setViewMode("kanban")}
                title="Vista kanban"
                className={viewMode === "kanban" ? "" : "text-muted-foreground"}
              >
                <KanbanSquareIcon className="size-4" />
              </Button>
            </div>

            {can("write", "shipments") && (
              <Button onClick={openCreate} className="gap-2">
                <PlusIcon className="size-4" /> Nuevo envío
              </Button>
            )}
          </div>
        </div>

        {/* ── Filter bar ── */}
        <div className="flex flex-col gap-2 rounded-lg border bg-muted/40 px-3 py-2.5">

          {/* Row 1: status • client autocomplete • worklines • date range • page size */}
          <div className="flex flex-wrap items-center gap-2">

            {/* Estado (activos/eliminados) — table only */}
            {viewMode === "table" && (
              <>
                <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v as FilterStatus); setPage(1) }}>
                  <SelectTrigger className="h-7 w-28 text-xs">
                    <SelectValue>
                      {filterStatus === "active" ? "Activos" : filterStatus === "deleted" ? "Eliminados" : "Todos"}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Activos</SelectItem>
                    <SelectItem value="deleted">Eliminados</SelectItem>
                    <SelectItem value="all">Todos</SelectItem>
                  </SelectContent>
                </Select>
                <div className="w-px h-5 bg-border shrink-0" />
              </>
            )}

            {/* Chofer */}
            <div className="w-44">
              <SearchableSelect
                compact
                emptyValue=""
                options={[
                  // Keep selected driver visible in trigger even before the user types
                  ...(filterDriver && !driverFilterOptions.find(d => d.name === filterDriver)
                    ? [{ value: filterDriver, label: filterDriver }]
                    : []),
                  ...driverFilterOptions.map(d => ({ value: d.name, label: d.name })),
                ]}
                value={filterDriver}
                onValueChange={(v) => { setFilterDriver(v); setPage(1) }}
                onSearchChange={onDriverSearchChange}
                placeholder="Todos los choferes"
                searchPlaceholder={driverIsMinLength ? "Buscando..." : "Escribí 3 letras para buscar"}
                emptyLabel={
                  driverInputValue.trim().length > 0 && !driverIsMinLength
                    ? "Escribí al menos 3 letras"
                    : "Sin resultados"
                }
              />
            </div>

            {/* Operadores */}
            {uniqueOperatorNames.length > 0 && (
              <div className="w-44">
                <SearchableSelect
                  multiple
                  compact
                  options={uniqueOperatorNames.map(n => ({ value: n, label: n }))}
                  value={filterOperatorNames}
                  onValueChange={(v) => { setFilterOperatorNames(v); setPage(1) }}
                  placeholder="Todos los operadores"
                  searchPlaceholder="Buscar operador..."
                />
              </div>
            )}

            {/* Cliente */}
            <div className="w-44">
              <SearchableSelect
                compact
                emptyValue="__all__"
                options={[
                  { value: "__all__", label: "Todos los clientes" },
                  ...clientOptions.map(c => ({ value: c.id, label: c.name, sublabel: c.code })),
                ]}
                value={filterClientId || "__all__"}
                onValueChange={(v) => { setFilterClientId(v === "__all__" ? "" : v); setPage(1) }}
                onSearchChange={onClientSearchChange}
                placeholder="Todos los clientes"
                searchPlaceholder="Buscar cliente..."
              />
            </div>

            <div className="w-px h-5 bg-border shrink-0" />

            {/* Facturación */}
            <Select value={filterBilling} onValueChange={(v) => { setFilterBilling(v as BillingStatus | "all"); setPage(1) }}>
              <SelectTrigger className="h-7 w-32 text-xs">
                <SelectValue>
                  {filterBilling === "all" ? "Facturación" : BILLING_STATUS_LABELS[filterBilling]}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {ALL_BILLING_STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{BILLING_STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Remito */}
            <Select value={filterNoteStatus || "__all__"} onValueChange={(v) => { setFilterNoteStatus(v === "__all__" ? "" : v as DeliveryNoteStatus); setPage(1) }}>
              <SelectTrigger className="h-7 w-32 text-xs">
                <SelectValue>
                  {filterNoteStatus === "normal"   ? "Sin validar"
                   : filterNoteStatus === "validado"  ? "Validado"
                   : filterNoteStatus === "reclamado" ? "Reclamado"
                   : "Remito: todos"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Remito: todos</SelectItem>
                <SelectItem value="normal">Sin validar</SelectItem>
                <SelectItem value="validado">Validado</SelectItem>
                <SelectItem value="reclamado">Reclamado</SelectItem>
              </SelectContent>
            </Select>

            {viewMode === "kanban" && (
              <button type="button"
                onClick={() => setFilterRouted(v => v === "routed" ? "unrouted" : v === "unrouted" ? "all" : "routed")}
                className={cn(
                  "h-7 inline-flex items-center gap-1.5 rounded-full px-2.5 text-xs font-medium transition-colors shrink-0",
                  filterRouted === "routed"
                    ? "bg-emerald-100 text-emerald-700 border border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800"
                    : filterRouted === "unrouted"
                      ? "bg-muted text-muted-foreground border border-border"
                      : "bg-background text-muted-foreground border border-border hover:bg-muted",
                )}
                title={
                  filterRouted === "routed" ? "Solo ruteadas — click para ver no ruteadas" :
                  filterRouted === "unrouted" ? "Solo no ruteadas — click para ver todas" :
                  "Filtrar por ruteado"
                }
              >
                <RouteIcon className="size-3 shrink-0" />
                {filterRouted === "routed" ? "Ruteadas" : filterRouted === "unrouted" ? "No ruteadas" : "Ruteado"}
              </button>
            )}
            {viewMode === "kanban" && (
              <button type="button" onClick={() => setFilterOnlyAlerts(v => !v)}
                className={cn(
                  "h-7 inline-flex items-center gap-1.5 rounded-full px-2.5 text-xs font-medium transition-colors shrink-0",
                  filterOnlyAlerts
                    ? "bg-red-100 text-red-700 border border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800"
                    : "bg-background text-muted-foreground border border-border hover:bg-muted",
                )}
                title={filterOnlyAlerts ? "Mostrando solo alertas — click para ver todas" : "Filtrar solo con alertas activas"}
              >
                <TriangleAlertIcon className="size-3 shrink-0" />
                Solo alertas
              </button>
            )}

            <div className="w-px h-5 bg-border shrink-0" />

            {/* Líneas */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-muted-foreground shrink-0">Líneas:</span>
              <div className="flex flex-wrap gap-1">
                {WORK_LINES.map(wl => (
                  <button key={wl} type="button"
                    onClick={() => setFilterWorkLines(prev =>
                      prev.includes(wl) ? prev.filter(x => x !== wl) : [...prev, wl]
                    )}
                    className={`h-7 inline-flex items-center text-xs px-2.5 rounded-full font-medium transition-colors ${
                      filterWorkLines.includes(wl)
                        ? WORK_LINE_COLORS[wl]
                        : "bg-background text-muted-foreground border border-border hover:bg-muted"
                    }`}
                  >
                    {WORK_LINE_LABELS[wl]}
                  </button>
                ))}
                {filterWorkLines.length > 0 && (
                  <button type="button" onClick={() => setFilterWorkLines([])}
                    className="h-7 text-xs text-muted-foreground hover:text-foreground underline underline-offset-2">
                    limpiar
                  </button>
                )}
              </div>
            </div>

            {viewMode === "table" && (
              <>
                <div className="w-px h-5 bg-border shrink-0" />

                {/* Rango de fechas */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <CalendarIcon className="size-3.5 text-muted-foreground shrink-0" />
                  <input type="date" value={originDateFrom}
                    onChange={(e) => setOriginDateFrom(e.target.value)}
                    title="Salida origen — desde"
                    className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground [color-scheme:light] dark:[color-scheme:dark] focus:outline-none focus:ring-1 focus:ring-ring w-32"
                  />
                  <span className="text-xs text-muted-foreground">–</span>
                  <input type="date" value={originDateTo}
                    onChange={(e) => setOriginDateTo(e.target.value)}
                    title="Salida origen — hasta"
                    className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground [color-scheme:light] dark:[color-scheme:dark] focus:outline-none focus:ring-1 focus:ring-ring w-32"
                  />
                  {(originDateFrom || originDateTo) && (
                    <button type="button" onClick={() => { setOriginDateFrom(""); setOriginDateTo("") }}
                      className="text-muted-foreground hover:text-foreground" title="Limpiar fechas">
                      <XIcon className="size-3.5" />
                    </button>
                  )}
                </div>

                {/* Page size — right */}
                <div className="flex items-center gap-1 ml-auto shrink-0">
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <button key={size} type="button"
                      onClick={() => { setPageSize(size); setPage(1) }}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        pageSize === size ? "bg-primary text-primary-foreground" : "hover:bg-muted text-muted-foreground"
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Row 2: status chips (table only) */}
          {viewMode === "table" && (
            <div className="flex flex-wrap items-center gap-1.5">
              {ALL_STATUSES.map((s) => {
                const active = filterStatuses.includes(s)
                return (
                  <button key={s} type="button" onClick={() => toggleStatus(s)}
                    className={cn(
                      "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium transition-opacity",
                      STATUS_COLORS[s],
                      active ? "opacity-100" : "opacity-30",
                    )}
                  >
                    {STATUS_LABELS[s]}
                  </button>
                )
              })}
              {filterStatuses.length < ALL_STATUSES.length && (
                <button type="button" onClick={() => setFilterStatuses([...ALL_STATUSES])}
                  className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground">
                  ver todos
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Selection action bar ── */}
        {selectedIds.size > 0 && viewMode === "table" && (
          <div className="sticky top-0 z-20 flex items-center gap-2 rounded-lg border bg-background/95 backdrop-blur-sm px-3 py-2 shadow-md">

            {/* Badge contador */}
            <span className="flex items-center gap-1.5 shrink-0">
              <span className="inline-flex items-center justify-center size-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold tabular-nums">
                {selectedIds.size}
              </span>
              <span className="text-sm font-medium text-foreground">
                carga{selectedIds.size !== 1 ? "s" : ""} seleccionada{selectedIds.size !== 1 ? "s" : ""}
              </span>
            </span>

            <div className="w-px self-stretch bg-border mx-1" />

            {/* Acciones de estado y facturación */}
            {can("write", "shipments") && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    disabled={bulkStatusPending}
                    render={
                      <Button variant="outline" size="sm" className="gap-1.5 h-7 text-xs">
                        <CircleCheckIcon className="size-3" />
                        Estado
                        <ChevronDownIcon className="size-3" />
                      </Button>
                    }
                  />
                  <DropdownMenuContent align="start" className="w-44">
                    {EDITABLE_STATUSES.map(s => (
                      <DropdownMenuItem
                        key={s}
                        onClick={() => confirmThen(
                          "Cambiar estado",
                          `¿Cambiás el estado de ${selectedIds.size} carga${selectedIds.size !== 1 ? "s" : ""} a «${STATUS_LABELS[s]}»?`,
                          () => handleBulkStatusUpdate({ status: s }),
                        )}
                      >
                        <span className={cn("size-2 rounded-full shrink-0", STATUS_COLORS[s].split(" ")[0])} />
                        {STATUS_LABELS[s]}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <DropdownMenu>
                  <DropdownMenuTrigger
                    disabled={bulkStatusPending}
                    render={
                      <Button variant="outline" size="sm" className="gap-1.5 h-7 text-xs">
                        <ReceiptIcon className="size-3" />
                        Facturación
                        <ChevronDownIcon className="size-3" />
                      </Button>
                    }
                  />
                  <DropdownMenuContent align="start" className="w-44">
                    {ALL_BILLING_STATUSES.map(s => (
                      <DropdownMenuItem
                        key={s}
                        onClick={() => confirmThen(
                          "Cambiar facturación",
                          `¿Cambiás la facturación de ${selectedIds.size} carga${selectedIds.size !== 1 ? "s" : ""} a «${BILLING_STATUS_LABELS[s]}»?\n\nEsta acción también actualizará todos los viajes asociados a ${selectedIds.size === 1 ? "esa carga" : "esas cargas"}.`,
                          () => handleBulkStatusUpdate({ billingStatus: s === "pendiente" ? null : s as "controlado" | "liquidado" | "facturado" | "cerrado" }),
                        )}
                      >
                        <span className={cn("size-2 rounded-full shrink-0", BILLING_STATUS_COLORS[s].split(" ")[0])} />
                        {BILLING_STATUS_LABELS[s]}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <div className="w-px self-stretch bg-border mx-1" />
              </>
            )}

            {/* Exportar */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide shrink-0">Exportar:</span>
              <Button variant="ghost" size="sm" className="gap-1 h-7 text-xs px-2 hover:bg-muted" onClick={() => handleShipmentExport("cargo-data")}>
                <PackageIcon className="size-3" /> Datos de carga
              </Button>
              <Button variant="ghost" size="sm" className="gap-1 h-7 text-xs px-2 hover:bg-muted" onClick={() => handleShipmentExport("cargo-documents")}>
                <FileTextIcon className="size-3" /> Documentos
              </Button>
              <Button variant="ghost" size="sm" className="gap-1 h-7 text-xs px-2 hover:bg-muted" onClick={() => handleShipmentExport("valuation")}>
                <CalculatorIcon className="size-3" /> Valorización
              </Button>
              <Button variant="ghost" size="sm" className="gap-1 h-7 text-xs px-2 hover:bg-muted" onClick={() => handleShipmentExport("trip-data")}>
                <TruckIcon className="size-3" /> Datos de viaje
              </Button>
              {canExportInternationalClientReport && (
                <Button variant="ghost" size="sm" className="gap-1 h-7 text-xs px-2 hover:bg-muted" onClick={() => handleShipmentExport("international-client-report")}>
                  <FileTextIcon className="size-3" /> Informe al cliente
                </Button>
              )}
            </div>

            {/* Cerrar */}
            <Button
              variant="ghost" size="sm" className="h-7 w-7 p-0 ml-auto shrink-0 text-muted-foreground hover:text-foreground"
              onClick={() => setSelectedIds(new Set())}
              title="Deseleccionar todo"
            >
              <XIcon className="size-3.5" />
            </Button>
          </div>
        )}

        {/* ── Table / Kanban ── */}
        {viewMode === "kanban" ? (
          <ShipmentsKanban
            items={kanbanFiltered}
            isAdmin={can("write", "shipments")}
            changingStatusId={changingStatusId}
            alertConfig={appConfig?.shipments}
            onEdit={openEdit}
            onDelete={openDelete}
            onStatusChange={(id, status) => statusMut.mutate({ id, status })}
            onView={(id) => navigate(`/shipments/${id}`)}
            onToggleRouted={(id, isRouted) => toggleRoutedMut.mutate({ id, isRouted })}
          />
        ) : (
        <>
        {serverTotal > 0 && (
          <div className="flex items-center justify-between text-sm text-muted-foreground px-1">
            <span>{(page - 1) * pageSize + 1}–{Math.min(page * pageSize, serverTotal)} de {serverTotal} resultados</span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
                className="p-1 rounded hover:bg-muted disabled:opacity-40 disabled:pointer-events-none"
              >
                <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              <span className="min-w-[4rem] text-center tabular-nums">{page} / {totalPages}</span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
                className="p-1 rounded hover:bg-muted disabled:opacity-40 disabled:pointer-events-none"
              >
                <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            </div>
          </div>
        )}
        <div className="rounded-md border bg-card">
          <div className="overflow-x-auto">
          <Table className="min-w-[960px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[36px] pr-0">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected }}
                    onChange={toggleSelectAll}
                    className="size-3.5 rounded border-border cursor-pointer accent-primary"
                    title="Seleccionar página"
                  />
                </TableHead>
                <TableHead className="w-[56px]">
                  <button
                    type="button"
                    onClick={() => toggleSort("internalId")}
                    className="flex items-center gap-1 hover:text-foreground transition-colors"
                  >
                    N°
                    {sortField === "internalId"
                      ? sortDir === "asc" ? <ChevronUpIcon className="size-3" /> : <ChevronDownIcon className="size-3" />
                      : <ChevronsUpDownIcon className="size-3 opacity-40" />}
                  </button>
                </TableHead>
                <TableHead>Remitente → Ciudad</TableHead>
                <TableHead>Destinatario → Ciudad</TableHead>
                <TableHead className="w-[90px] text-right">
                  <button
                    type="button"
                    onClick={() => toggleSort("unloadingWeightKg")}
                    className="flex items-center justify-end gap-1 w-full hover:text-foreground transition-colors"
                  >
                    Tons.
                    {sortField === "unloadingWeightKg"
                      ? sortDir === "asc" ? <ChevronUpIcon className="size-3" /> : <ChevronDownIcon className="size-3" />
                      : <ChevronsUpDownIcon className="size-3 opacity-40" />}
                  </button>
                </TableHead>
                <TableHead className="w-[110px]">Remito</TableHead>
                <TableHead className="w-[90px]">
                  <button
                    type="button"
                    onClick={() => toggleSort("scheduledOriginArrivalAt")}
                    className="flex items-center gap-1 hover:text-foreground transition-colors"
                  >
                    Salida
                    {sortField === "scheduledOriginArrivalAt"
                      ? sortDir === "asc" ? <ChevronUpIcon className="size-3" /> : <ChevronDownIcon className="size-3" />
                      : <ChevronsUpDownIcon className="size-3 opacity-40" />}
                  </button>
                </TableHead>
                <TableHead className="w-[60px]">Cli.</TableHead>
                <TableHead>Chofer</TableHead>
                <TableHead className="w-[140px]">Estado</TableHead>
                <TableHead className="w-7" />
                <TableHead className="w-7" />
                {can("write", "shipments") && <TableHead className="w-9" />}
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={can("write", "shipments") ? 14 : 13} className="h-24 text-center text-muted-foreground">
                    Cargando cargas...
                  </TableCell>
                </TableRow>
              ) : shipmentsData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={can("write", "shipments") ? 14 : 13} className="h-24 text-center text-muted-foreground">
                    No se encontraron cargas.
                  </TableCell>
                </TableRow>
              ) : (
                shipmentsData.flatMap((d) => {
                  const isExpanded = expandedIds.has(d.id)
                  return [
                  <TableRow
                    key={d.id}
                    className={cn(
                      d.deletedAt ? "opacity-50 italic" : undefined,
                      selectedIds.has(d.id) ? "bg-muted/50" : undefined,
                    )}
                  >
                    <TableCell className="pr-0">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(d.id)}
                        onChange={() => toggleSelect(d.id)}
                        className="size-3.5 rounded border-border accent-primary cursor-pointer"
                      />
                    </TableCell>
                    {/* N° */}
                    <TableCell>
                      <Link
                        to={`/shipments/${d.id}`}
                        className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded hover:bg-muted/70 transition-colors"
                      >
                        #{d.internalId}
                      </Link>
                      {(d.internationalIds ?? []).length > 0 && (
                        <div className="mt-0.5 text-[10px] text-muted-foreground font-mono leading-tight">
                          {(d.internationalIds ?? []).map(value => (
                            <div key={value}>{value}</div>
                          ))}
                        </div>
                      )}

                    </TableCell>

                    {/* Remitente → Ciudad / País */}
                    <TableCell>
                      {can("write", "shipments") && !d.deletedAt ? (
                        <button
                          type="button"
                          onClick={() => openQuickEdit("sender", d)}
                          className="group flex items-center gap-1 text-left"
                          title="Click para editar remitente"
                        >
                          {d.senderName
                            ? <span className="font-medium text-sm leading-tight group-hover:text-primary group-hover:underline transition-colors">{d.senderName}</span>
                            : <span className="text-muted-foreground/30 text-xs group-hover:text-primary transition-colors">— asignar</span>}
                          <PencilIcon className="size-2.5 opacity-0 group-hover:opacity-40 transition-opacity shrink-0" />
                        </button>
                      ) : (
                        d.senderName
                          ? <div className="font-medium text-sm leading-tight">{d.senderName}</div>
                          : <span className="text-muted-foreground/30 text-xs">—</span>
                      )}
                      {(d.senderCityName || d.senderCountryName) && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                          <ArrowRightIcon className="size-3 shrink-0" />
                          <span>{[d.senderCityName, d.senderCountryName].filter(Boolean).join(" / ")}</span>
                        </div>
                      )}
                    </TableCell>

                    {/* Destinatario → Ciudad / País */}
                    <TableCell>
                      {can("write", "shipments") && !d.deletedAt ? (
                        <button
                          type="button"
                          onClick={() => openQuickEdit("recipient", d)}
                          className="group flex items-center gap-1 text-left"
                          title="Click para editar destinatario"
                        >
                          {d.recipientName
                            ? <span className="font-medium text-sm leading-tight group-hover:text-primary group-hover:underline transition-colors">{d.recipientName}</span>
                            : <span className="text-muted-foreground/30 text-xs group-hover:text-primary transition-colors">— asignar</span>}
                          <PencilIcon className="size-2.5 opacity-0 group-hover:opacity-40 transition-opacity shrink-0" />
                        </button>
                      ) : (
                        d.recipientName
                          ? <div className="font-medium text-sm leading-tight">{d.recipientName}</div>
                          : <span className="text-muted-foreground/30 text-xs">—</span>
                      )}
                      {(d.recipientCityName || d.recipientCountryName) && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                          <ArrowRightIcon className="size-3 shrink-0" />
                          <span>{[d.recipientCityName, d.recipientCountryName].filter(Boolean).join(" / ")}</span>
                        </div>
                      )}
                      {!d.recipientName && !d.recipientCityName && can("write", "shipments") && !d.deletedAt ? null : (
                        !d.recipientName && !d.recipientCityName ? <span className="text-muted-foreground/30 text-xs">—</span> : null
                      )}
                      {/* Route information */}
                      {d.workLine === "nacional" && (d.routeId || d.senderCityName || d.recipientCityName) ? (
                        d.routeId ? (
                          <ShipmentStopsHoverCard shipmentId={d.id}>
                            <span className="mt-2 grid gap-1 rounded-md border border-blue-200 bg-blue-50/60 p-1.5 text-[10px] dark:border-blue-900/50 dark:bg-blue-950/20">
                              <span className="flex items-center gap-1 text-muted-foreground">
                                <PackageIcon className="size-3 shrink-0" />
                                <span className="truncate">{formatRoutePath(d.senderCityName, null, d.recipientCityName, d.intermediateStopStateNames) || "—"}</span>
                              </span>
                              <span className="flex items-center gap-1 text-blue-800 dark:text-blue-300">
                                <TruckIcon className="size-3 shrink-0" />
                                <span className="truncate">{formatRoutePath(d.routeOriginCityName || d.senderCityName, d.routeName, d.routeTargetStateName || d.routeTargetCityName || d.recipientCityName, d.intermediateStopStateNames) || "—"}</span>
                              </span>
                            </span>
                          </ShipmentStopsHoverCard>
                        ) : (
                          <div className="mt-2 grid gap-1 rounded-md border border-blue-200 bg-blue-50/60 p-1.5 text-[10px] dark:border-blue-900/50 dark:bg-blue-950/20">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <PackageIcon className="size-3 shrink-0" />
                              <span className="truncate">{formatRoutePath(d.senderCityName, null, d.recipientCityName, d.intermediateStopStateNames) || "—"}</span>
                            </div>
                            <div className="flex items-center gap-1 text-blue-800 dark:text-blue-300">
                              <TruckIcon className="size-3 shrink-0" />
                              <span className="truncate">{formatRoutePath(d.routeOriginCityName || d.senderCityName, d.routeName, d.routeTargetStateName || d.routeTargetCityName || d.recipientCityName, d.intermediateStopStateNames) || "—"}</span>
                            </div>
                          </div>
                        )
                      ) : d.shipmentKm != null ? (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                          <RouteIcon className="size-3 shrink-0" />
                          <span className="tabular-nums">{d.shipmentKm.toLocaleString("es-AR")} km</span>
                        </div>
                      ) : (d.senderCityName && d.recipientCityName) ? (
                        <div className="flex items-center gap-1 text-xs text-amber-500 mt-0.5">
                          <TriangleAlertIcon className="size-3 shrink-0" />
                          <span>Sin ruta registrada</span>
                        </div>
                      ) : null}
                    </TableCell>

                    {/* Toneladas carga / descarga / diferencia */}
                    <TableCell className="text-right">
                      {(() => {
                        const load = d.loadingWeightKg ? parseFloat(d.loadingWeightKg) / 1000 : null
                        const unload = d.unloadingWeightKg ? parseFloat(d.unloadingWeightKg) / 1000 : null
                        const diff = load !== null && unload !== null ? unload - load : null
                        const diffR = diff !== null ? Math.round(diff * 100) / 100 : null
                        const fmt = (v: number) => v.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        if (!load && !unload) return <span className="text-muted-foreground/30 text-xs">—</span>
                        return (
                          <div className="flex flex-col items-end gap-0.5">
                            {load !== null
                              ? <span className="tabular-nums text-xs font-medium text-muted-foreground">{fmt(load)} t ↑</span>
                              : <span className="text-muted-foreground/30 text-xs">— ↑</span>}
                            {unload !== null
                              ? <span className="tabular-nums text-xs font-medium">{fmt(unload)} t ↓</span>
                              : <span className="text-muted-foreground/30 text-xs">— ↓</span>}
                            {diffR !== null && (
                              <span className={`tabular-nums text-[11px] font-semibold ${
                                diffR === 0 ? "text-muted-foreground" :
                                diffR < 0 ? "text-red-500" : "text-emerald-600"
                              }`}>
                                {diffR > 0 ? "+" : ""}{fmt(diffR)} t
                              </span>
                            )}
                          </div>
                        )
                      })()}
                    </TableCell>

                    {/* N° Remito + estado badge debajo */}
                    <TableCell>
                      {d.deliveryNoteNumber ? (
                        <div className="flex flex-col items-start gap-1">
                          <span className="font-mono text-xs">{d.deliveryNoteNumber}</span>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger
                                type="button"
                                onClick={() => {
                                  const current = d.deliveryNoteStatus ?? "normal"
                                  remitoStatusMut.mutate({ id: d.id, status: DELIVERY_NOTE_STATUS_NEXT[current] })
                                }}
                                className={cn(
                                  "inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium transition-colors",
                                  DELIVERY_NOTE_STATUS_CLASS[d.deliveryNoteStatus ?? "normal"],
                                  DELIVERY_NOTE_STATUS_HOVER_CLASS[d.deliveryNoteStatus ?? "normal"],
                                )}
                              >
                                {DELIVERY_NOTE_STATUS_LABEL[d.deliveryNoteStatus ?? "normal"]}
                              </TooltipTrigger>
                              <TooltipContent side="top">Click para cambiar estado</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      ) : <span className="text-muted-foreground/30 text-xs">—</span>}
                    </TableCell>

                    {/* Salida origen — date and time stacked, + loading window for batea */}
                    <TableCell>
                      <div className="flex flex-col leading-tight gap-0.5">
                        {d.scheduledOriginArrivalAt ? (() => {
                          const dt = new Date(d.scheduledOriginArrivalAt)
                          const pad = (n: number) => String(n).padStart(2, "0")
                          const date = `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}/${String(dt.getFullYear()).slice(-2)}`
                          const time = `${pad(dt.getHours())}:${pad(dt.getMinutes())}`
                          return (
                            <>
                              <span className="tabular-nums text-xs text-muted-foreground">{date}</span>
                              <span className="tabular-nums text-[11px] text-muted-foreground/70">{time}</span>
                            </>
                          )
                        })() : <span className="text-muted-foreground/30 text-xs">—</span>}
                        {d.workLine === "batea" && (() => {
                          const win = BATEA_LOADING_WINDOWS.find(w => w.slug === d.loadingWindow)
                          const canEdit = can("write", "shipments") && !d.deletedAt
                          if (!win && !canEdit) return null
                          return canEdit ? (
                            <button
                              type="button"
                              onClick={() => openQuickEdit("loadingWindow", d)}
                              className="group flex items-center gap-0.5"
                              title="Click para editar ventana de carga"
                            >
                              {win
                                ? <span className="tabular-nums text-[10px] text-sky-600 font-medium group-hover:underline transition-colors">{win.label}</span>
                                : <span className="tabular-nums text-[10px] text-muted-foreground/40 group-hover:text-sky-600 transition-colors">— ventana</span>}
                              <PencilIcon className="size-2 opacity-0 group-hover:opacity-40 transition-opacity shrink-0" />
                            </button>
                          ) : win ? (
                            <span className="tabular-nums text-[10px] text-sky-600 font-medium">{win.label}</span>
                          ) : null
                        })()}
                      </div>
                    </TableCell>

                    {/* Cliente */}
                    <TableCell>
                      <ClientAvatar clientId={d.clientId} clientName={d.clientName} />
                    </TableCell>

                    {/* Chofer */}
                    <TableCell>
                      {(() => {
                        const canQuickEdit = can("write", "shipments") && !d.deletedAt
                        if (d.driverNames) {
                          const driverList2 = d.driverNames.split("||").filter(Boolean)
                          const last        = driverList2[driverList2.length - 1]
                          const inner = (
                            <div className={cn("flex items-center gap-1.5", canQuickEdit && "group")}>
                              <AvatarGroup>
                                {driverList2.map((name) => (
                                  <Avatar key={name} size="sm">
                                    <AvatarFallback style={{ backgroundColor: avatarColor(name), color: "white" }}>
                                      {avatarInitials(name)}
                                    </AvatarFallback>
                                  </Avatar>
                                ))}
                              </AvatarGroup>
                              <span className={cn(
                                "text-xs text-muted-foreground",
                                canQuickEdit && "group-hover:text-primary transition-colors",
                              )}>{last}</span>
                              {/* Travel advance indicator */}
                              <span
                                title={d.firstTripHasTravelAdvance ? `Anticipo: ${d.firstTripHasTravelAdvance}` : "Sin anticipo"}
                                className={cn(
                                  "inline-flex items-center justify-center size-3.5 rounded-full text-[9px] font-bold shrink-0",
                                  d.firstTripHasTravelAdvance
                                    ? "bg-green-100 text-green-700"
                                    : "bg-muted text-muted-foreground/50"
                                )}
                              >
                                A
                              </span>
                              {canQuickEdit && <PencilIcon className="size-2.5 opacity-0 group-hover:opacity-40 transition-opacity shrink-0" />}
                            </div>
                          )
                          return canQuickEdit ? (
                            <button type="button" className="text-left" title="Click para cambiar móvil" onClick={() => openQuickEdit("mobile", d)}>{inner}</button>
                          ) : inner
                        }
                        return canQuickEdit ? (
                          <button
                            type="button"
                            onClick={() => openQuickEdit("mobile", d)}
                            className="text-muted-foreground/30 text-xs hover:text-primary transition-colors"
                            title="Click para asignar móvil"
                          >
                            — asignar
                          </button>
                        ) : (
                          <span className="text-muted-foreground/30 text-xs">—</span>
                        )
                      })()}
                      {/* Truck + trailer plates inline, each with its own copy button */}
                      {(d.firstTripTruckPlate || d.firstTripTrailerPlate) && (
                        <div className="flex items-center gap-2 mt-0.5">
                          <TruckIcon className="size-2.5 text-muted-foreground/40 shrink-0" />
                          {d.firstTripTruckPlate && (
                            <div className="flex items-center gap-0.5 group/truck">
                              <span className="text-[10px] text-muted-foreground/60 font-mono">{d.firstTripTruckPlate}</span>
                              <button
                                type="button"
                                title="Copiar patente camión"
                                onClick={() => {
                                  navigator.clipboard.writeText(d.firstTripTruckPlate!)
                                  toast.success("Patente copiada", { description: d.firstTripTruckPlate!, duration: 2000 })
                                }}
                                className="text-muted-foreground/30 hover:text-muted-foreground transition-colors opacity-0 group-hover/truck:opacity-100"
                              >
                                <CopyIcon className="size-2.5" />
                              </button>
                            </div>
                          )}
                          {d.firstTripTruckPlate && d.firstTripTrailerPlate && (
                            <span className="text-[10px] text-muted-foreground/30">/</span>
                          )}
                          {d.firstTripTrailerPlate && (
                            <div className="flex items-center gap-0.5 group/trailer">
                              <span className="text-[10px] text-muted-foreground/60 font-mono">{d.firstTripTrailerPlate}</span>
                              <button
                                type="button"
                                title="Copiar patente semi"
                                onClick={() => {
                                  navigator.clipboard.writeText(d.firstTripTrailerPlate!)
                                  toast.success("Patente copiada", { description: d.firstTripTrailerPlate!, duration: 2000 })
                                }}
                                className="text-muted-foreground/30 hover:text-muted-foreground transition-colors opacity-0 group-hover/trailer:opacity-100"
                              >
                                <CopyIcon className="size-2.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </TableCell>

                    {/* Estado + Facturación */}
                    <TableCell>
                      <div className="flex flex-col gap-1 items-start">
                        {/* Badge estado operativo — clickeable si puede editar */}
                        {can("write", "shipments") && !d.deletedAt ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger render={
                              <button className={cn(
                                "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap cursor-pointer hover:opacity-80 transition-opacity",
                                STATUS_COLORS[d.status],
                              )}>
                                {STATUS_LABELS[d.status]}
                              </button>
                            } />
                            <DropdownMenuContent align="start" className="w-44">
                              {EDITABLE_STATUSES.filter(s => s !== d.status).map(s => (
                                <DropdownMenuItem
                                  key={s}
                                  onClick={() => confirmThen(
                                    "Cambiar estado",
                                    `¿Cambiás el estado de la carga #${d.internalId} a «${STATUS_LABELS[s]}»?`,
                                    async () => { await patchShipmentStatus(d.id, { status: s }) },
                                  )}
                                >
                                  <span className={cn("size-2 rounded-full shrink-0", STATUS_COLORS[s].split(" ")[0])} />
                                  {STATUS_LABELS[s]}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          <span className={cn(
                            "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap",
                            STATUS_COLORS[d.status],
                          )}>
                            {STATUS_LABELS[d.status]}
                          </span>
                        )}
                        {/* Badge facturación — clickeable si puede editar */}
                        {(() => {
                          const bs: BillingStatus = d.billingStatus ?? "pendiente"
                          const badge = (
                            <span className={cn(
                              "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap",
                              BILLING_STATUS_COLORS[bs],
                            )}>
                              {BILLING_STATUS_LABELS[bs]}
                            </span>
                          )
                          if (!can("write", "shipments") || d.deletedAt) return <>{bs === "pendiente" ? null : badge}</>
                          return (
                            <>
                              <DropdownMenu>
                                <DropdownMenuTrigger render={
                                  <button className={cn(
                                    "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap cursor-pointer hover:opacity-80 transition-opacity",
                                    BILLING_STATUS_COLORS[bs],
                                  )}>
                                    {BILLING_STATUS_LABELS[bs]}
                                  </button>
                                } />
                                <DropdownMenuContent align="start" className="w-44">
                                  {ALL_BILLING_STATUSES.filter(s => s !== bs).map(s => (
                                    <DropdownMenuItem
                                      key={s}
                                      onClick={() => confirmThen(
                                        "Cambiar facturación",
                                        `¿Cambiás la facturación de la carga #${d.internalId} a «${BILLING_STATUS_LABELS[s]}»?\n\nEsta acción también actualizará todos los viajes asociados a esa carga.`,
                                        async () => { await patchShipmentStatus(d.id, { billingStatus: s === "pendiente" ? null : s as "controlado" | "liquidado" | "facturado" | "cerrado" }) },
                                      )}
                                    >
                                      <span className={cn("size-2 rounded-full shrink-0", BILLING_STATUS_COLORS[s].split(" ")[0])} />
                                      {BILLING_STATUS_LABELS[s]}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </>
                          )
                        })()
                        }
                      </div>
                    </TableCell>

                    {/* Ruteado */}
                    <TableCell className="px-2">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger
                            type="button"
                            onClick={() => can("write", "shipments") && !d.deletedAt && toggleRoutedMut.mutate({ id: d.id, isRouted: !d.isRouted })}
                            className={cn(
                              "inline-flex p-1.5 -m-1.5 rounded transition-colors",
                              can("write", "shipments") && !d.deletedAt ? "cursor-pointer" : "cursor-default",
                              d.isRouted
                                ? "text-emerald-500 hover:text-emerald-600"
                                : "text-muted-foreground/20 hover:text-muted-foreground/50",
                            )}
                          >
                            <RouteIcon className="size-3.5" />
                          </TooltipTrigger>
                          <TooltipContent side="left">
                            {d.isRouted ? "Ruteado — cliente confirmó camión y chofer" : "No ruteado"}
                            {can("write", "shipments") && !d.deletedAt && " · click para cambiar"}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>

                    {/* Observaciones */}
                    <TableCell className="px-2">
                      {can("write", "shipments") && !d.deletedAt ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger
                              type="button"
                              onClick={() => openQuickEdit("notes", d)}
                              className={cn(
                                "inline-flex p-1.5 -m-1.5 rounded transition-colors",
                                d.notes
                                  ? "text-amber-500 hover:text-amber-600"
                                  : "text-muted-foreground/20 hover:text-muted-foreground/50",
                              )}
                            >
                              <MessageSquareIcon className="size-3.5" />
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-[220px] text-xs whitespace-pre-wrap">
                              {d.notes ? d.notes : "Sin observaciones — click para agregar"}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : d.notes ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger className="inline-flex p-1.5 -m-1.5 rounded text-amber-500 cursor-default">
                              <MessageSquareIcon className="size-3.5" />
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-[220px] text-xs whitespace-pre-wrap">
                              {d.notes}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className="inline-flex text-muted-foreground/20">
                          <MessageSquareIcon className="size-3.5" />
                        </span>
                      )}
                    </TableCell>

                    {/* Acciones */}
                    {can("write", "shipments") && (
                      <TableCell className="text-right pr-2">
                        {d.deletedAt ? (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => restoreMut.mutate(d.id)}
                            disabled={restoringId === d.id}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            title="Restaurar"
                          >
                            <RestoreIcon className="size-3.5" />
                          </Button>
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger
                              render={
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  className="text-muted-foreground"
                                />
                              }
                            >
                              <MoreVerticalIcon className="size-4" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                              <DropdownMenuItem onClick={() => navigate(`/shipments/${d.id}`)}>
                                <EyeIcon className="size-4" />
                                Ver detalle
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEdit(d)}>
                                <PencilIcon className="size-4" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openDuplicate(d)}
                                className="text-sky-600 focus:text-sky-600"
                              >
                                <CopyIcon className="size-4" />
                                Duplicar
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openCancel(d)}
                                className="text-red-600 focus:text-red-600"
                              >
                                <BanIcon className="size-4" />
                                Cancelar
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => openDelete(d)}
                                variant="destructive"
                              >
                                <Trash2Icon className="size-4" />
                                Eliminar
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    )}

                    {/* Expand chevron */}
                    <TableCell className="px-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        onClick={() => toggleExpand(d.id)}
                        title={isExpanded ? "Colapsar viajes" : "Ver viajes"}
                      >
                        {isExpanded
                          ? <ChevronDownIcon className="size-4" />
                          : <ChevronRightIcon className="size-4" />
                        }
                      </Button>
                    </TableCell>
                  </TableRow>,

                  /* Expanded trips panel */
                  ...(isExpanded ? [
                    <TableRow key={`${d.id}-trips`} className="bg-muted/30 hover:bg-muted/30">
                      <TableCell colSpan={can("write", "shipments") ? 15 : 14} className="p-0">
                        <div className="px-6 py-3">
                          <div className="rounded-md border overflow-hidden">
                            <div className="px-4 py-2 border-b bg-background">
                              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Viajes</span>
                            </div>
                            <ShipmentTripsPanel shipmentId={d.id} />
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ] : []),
                ]})
              )}
            </TableBody>
          </Table>
          </div>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={serverTotal}
            pageSize={pageSize}
            onPageChange={setPage}
          />
        </div>
        </>
        )}
      </div>

      {/* ── Create / Edit sheet ── */}
      <Sheet open={sheetOpen} onOpenChange={(open) => { if (!open) closeSheet() }}>
        <SheetContent side="right" className="h-full min-h-0 w-[min(32rem,calc(100vw-1rem))] max-w-none overflow-hidden flex flex-col">
          <SheetHeader className="shrink-0">
            <SheetTitle>{editing ? "Editar envío" : "Nuevo envío"}</SheetTitle>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-4 pb-4 flex flex-col gap-4">

            {/* Cliente */}
            <div className="grid gap-1.5">
              <Label>Cliente <span className="text-destructive">*</span></Label>
              <SearchableSelect
                options={clientSelectOptions}
                value={form.clientId || "__none__"}
                onValueChange={(v) => setForm(f => ({ ...f, clientId: v === "__none__" ? "" : v }))}
                onSearchChange={onClientSearchChange}
                placeholder="Sin asignar"
                searchPlaceholder="Buscar cliente..."
              />
            </div>

            {/* Producto */}
            <div className="grid gap-1.5">
              <Label>Producto</Label>
              <SearchableSelect
                options={cargoProductSelectOptions}
                value={form.productId || "__none__"}
                onValueChange={(v) => setForm(f => ({ ...f, productId: v === "__none__" ? "" : v }))}
                placeholder="Sin producto"
                searchPlaceholder="Buscar producto..."
              />
            </div>

            {/* Línea de trabajo */}
            <div className="grid gap-1.5">
              <Label>Línea de trabajo <span className="text-destructive">*</span></Label>
              <div className="flex flex-wrap gap-2">
                {WORK_LINES.map(wl => (
                  <button
                    key={wl}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, workLine: f.workLine === wl ? "" : wl }))}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      form.workLine === wl
                        ? `${WORK_LINE_COLORS[wl]} border-current font-medium`
                        : "border-border text-muted-foreground hover:border-foreground"
                    }`}
                  >
                    {WORK_LINE_LABELS[wl]}
                  </button>
                ))}
              </div>
            </div>


            {/* Estado de facturación — solo al editar */}
            {editing && (
              <div className="space-y-1">
                <Label>Estado de facturación</Label>
                <Select
                  value={form.billingStatus ?? "pendiente"}
                  onValueChange={(v) =>
                    setForm(f => ({ ...f, billingStatus: v === "pendiente" ? null : v as "controlado" | "liquidado" | "facturado" | "cerrado" }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue>
                      {BILLING_STATUS_LABELS[form.billingStatus ?? "pendiente"]}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {ALL_BILLING_STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{BILLING_STATUS_LABELS[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Estado — solo al editar */}
            {editing && (
              <div className="grid gap-1.5">
                <Label>Estado</Label>
                <div className="flex flex-wrap gap-2">
                  {EDITABLE_STATUSES.map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, status: s }))}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors font-medium ${
                        form.status === s
                          ? `${STATUS_COLORS[s]} border-current`
                          : "border-border text-muted-foreground hover:border-foreground"
                      }`}
                    >
                      {STATUS_LABELS[s]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Remitente (party) */}
            <div className="grid gap-1.5">
              <Label>Remitente <span className="text-destructive">*</span></Label>
              <SearchableSelect
                options={senderSelectOptions}
                value={form.senderId || "__none__"}
                onValueChange={(v) => setRouteEndpoint("senderId", v === "__none__" ? "" : v)}
                placeholder="Sin asignar"
                searchPlaceholder="Buscar remitente..."
              />
            </div>

            {/* Destinatario (party) */}
            <div className="grid gap-1.5">
              <Label>Destinatario</Label>
              <SearchableSelect
                options={recipientSelectOptions}
                value={form.recipientId || "__none__"}
                onValueChange={(v) => setRouteEndpoint("recipientId", v === "__none__" ? "" : v)}
                placeholder="Sin asignar"
                searchPlaceholder="Buscar destinatario..."
              />
            </div>

            {form.workLine === "nacional" && (
              <div className="grid min-w-0 w-full max-w-full gap-3 rounded-lg border bg-muted/20 p-3">
                <NationalRouteEditor
                  senderId={form.senderId}
                  recipientId={form.recipientId}
                  routeId={form.routeId}
                  routeStops={form.routeStops}
                  routeOptions={routeOptions}
                  partyOptions={partyOptions}
                  onRouteIdChange={routeId => setForm(previous => ({ ...previous, routeId }))}
                  onRouteStopsChange={routeStops => setForm(previous => ({ ...previous, routeStops }))}
                />
                {form.senderId && form.recipientId && (
                  <div className="grid gap-2">
                    <ShipmentRouteInfoBlock
                      title="Datos de viaje"
                      origin={effectiveRoute?.originCityName ?? senderParty?.cityName}
                      destination={effectiveRoute?.targetCityName ?? recipientParty?.cityName}
                      km={effectiveRoute?.km}
                      code={effectiveRoute?.code}
                    />
                    <ShipmentRouteInfoBlock
                      title="Datos de carga"
                      origin={senderParty?.cityName}
                      destination={effectiveRoute?.targetCityName ?? recipientParty?.cityName}
                      km={cargoRoute?.km ?? editing?.shipmentKm}
                      code={cargoRoute?.code ?? editing?.cargoRouteCode}
                      showKm={false}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Salida proyectada */}
            <div className="grid gap-1.5">
              <Label htmlFor="sh-origin-at">Llegada proyectada a origen</Label>
              <Input
                id="sh-origin-at"
                type="datetime-local"
                value={form.scheduledOriginArrivalAt}
                onChange={(e) => {
                  const newOriginAt = e.target.value
                  setForm(f => {
                    const updated = { ...f, scheduledOriginArrivalAt: newOriginAt }
                    // Auto-complete destination with +44 hours if batea
                    if (f.workLine === "batea" && newOriginAt && !f.scheduledDestinationArrivalAt) {
                      const originDate = new Date(newOriginAt)
                      originDate.setHours(originDate.getHours() + 44)
                      updated.scheduledDestinationArrivalAt = originDate.toISOString().slice(0, 16)
                    }
                    return updated
                  })
                }}
              />
            </div>

            {/* Llegada proyectada */}
            <div className="grid gap-1.5">
              <Label htmlFor="sh-dest-at">Llegada proyectada a destino</Label>
              <Input
                id="sh-dest-at"
                type="datetime-local"
                value={form.scheduledDestinationArrivalAt}
                onChange={(e) => setForm(f => ({ ...f, scheduledDestinationArrivalAt: e.target.value }))}
              />
            </div>

            {/* Móvil */}
            {(() => {
              const multiTrip = editing != null && (editing.tripCount ?? 0) > 1
              return (
                <>
                  <div className="grid gap-1.5">
                    <Label>Móvil</Label>
                    <SearchableSelect
                      options={mobileEditor.mobileSelectOptions}
                      value={form.mobileId || "__none__"}
                      disabled={multiTrip}
                      onValueChange={(v) => {
                        const newId = v === "__none__" ? "" : v
                        setForm(f => ({ ...f, mobileId: newId }))
                        mobileEditor.onMobileSelected(newId)
                      }}
                      placeholder="Sin asignar"
                      searchPlaceholder="Buscar móvil..."
                    />
                    {multiTrip ? (
                      <p className="text-[11px] text-muted-foreground">
                        Este envío tiene {editing.tripCount} viajes — no es posible modificar el móvil desde aquí.
                      </p>
                    ) : (
                      <MobileEditorPanel mobileId={form.mobileId} editor={mobileEditor} />
                    )}
                  </div>
                </>
              )
            })()}

            {/* Batea configuration, visible only when workLine === "batea" */}
            {form.workLine === "batea" && (
              <BateaConfigSection form={form} setForm={setForm} finalDestinations={finalDestinationOptions} />
            )}

            {/* N° Externo */}
            <div className="grid gap-1.5">
              <Label htmlFor="sh-external-id">N° Externo</Label>
              <Input
                id="sh-external-id"
                value={form.externalId}
                onChange={(e) => setForm(f => ({ ...f, externalId: e.target.value }))}
                maxLength={100}
                placeholder="Identificador del cliente..."
              />
            </div>

            {form.workLine === "internacional" && (
              <InternationalShipmentIdsEditor
                values={form.internationalIds}
                corridor={nextInternationalIdQuery.data?.corridor ?? null}
                description="Corredor determinado por origen y destino. Los IDs deben respetar el prefijo configurado."
                showWarning={Boolean(form.senderId && form.recipientId)}
                suggestedNext={(values) => nextInternationalIdQuery.data?.seed
                  ? nextInternationalId(nextInternationalIdQuery.data.seed, values)
                  : null}
                nextValue={nextInternationalIdQuery.data?.next ?? null}
                autoAssignValue={editing ? null : nextInternationalIdQuery.data?.next ?? null}
                onChange={(internationalIds) => setForm(f => ({ ...f, internationalIds }))}
              />
            )}

            {/* Cargo value is available for international shipments in create and edit forms. */}
            {form.workLine === "internacional" && (
              <div className="grid gap-1.5">
                <Label htmlFor="sh-cargo-value">Valor de la mercadería (ARS)</Label>
                <Input
                  id="sh-cargo-value"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.cargoValue}
                  onChange={(e) => setForm(f => ({ ...f, cargoValue: e.target.value }))}
                  placeholder="Ingrese el valor en ARS..."
                />
              </div>
            )}

            {/* N° Remito */}
            <div className="grid gap-1.5">
              <Label htmlFor="sh-delivery-note">N° Remito</Label>
              <Input
                id="sh-delivery-note"
                value={form.deliveryNoteNumber}
                onChange={(e) => setForm(f => ({ ...f, deliveryNoteNumber: e.target.value }))}
                maxLength={100}
                placeholder="Número de remito..."
              />
            </div>

            {/* Observaciones */}
            <div className="grid gap-1.5">
              <Label htmlFor="sh-notes">Observaciones</Label>
              <textarea
                id="sh-notes"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Notas u observaciones del envío..."
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              />
            </div>

            {/* Cantidad de copias — solo al crear en batea */}
            {!editing && form.workLine === "batea" && (
              <div className="grid gap-1.5">
                <Label htmlFor="sh-copies">
                  Cantidad de copias
                  <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">(planificación masiva)</span>
                </Label>
                <Input
                  id="sh-copies"
                  type="number"
                  min={1}
                  max={50}
                  value={createCopies}
                  onChange={(e) => setCreateCopies(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                  className="w-24"
                />
                {createCopies > 1 && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
                    <p>Se crearán <strong>{createCopies} envíos idénticos</strong> con todos los datos del formulario. El único campo que no se aplica es el <strong>móvil</strong> — cada envío quedará sin móvil asignado.</p>
                  </div>
                )}
              </div>
            )}

            {formError && (
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">
                {formError}
              </p>
            )}

            <SheetFooter className="px-0 mt-auto pt-4">
              <Button type="button" variant="outline" onClick={closeSheet}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving
                  ? "Guardando..."
                  : editing
                    ? "Guardar cambios"
                    : createCopies > 1
                      ? `Crear ${createCopies} envíos`
                      : "Crear envío"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <QuickEditDialog
        target={quickEditTarget}
        onClose={() => setQuickEditTarget(null)}
        onSuccess={() => {}}
      />

      <MobileEditorConfirmDialog
        editor={mobileEditor}
        onConfirmed={async (driverId, trailerId) => {
          // Single-trip: also update the trip's driver/trailer directly
          if (editing && editing.tripCount <= 1 && editing.firstTripId) {
            await authorizedFetch(`${API_URL}/api/trips/${editing.firstTripId}`, {
              method:  "PATCH",
              headers: { "Content-Type": "application/json" },
              body:    JSON.stringify({
                driverId:  driverId  || null,
                trailerId: trailerId || null,
              }),
            })
            qc.invalidateQueries({ queryKey: ["trips"] })
          }
          mobileEditor.closeConfirm()
          doSave(pendingMobileSave ?? form)
          setPendingMobileSave(null)
        }}
      />

      {/* ── Duplicate dialog ── */}
      <Dialog
        open={duplicateOpen}
        onOpenChange={(open) => {
          if (!open) { setDuplicateOpen(false); setDuplicateTarget(null) }
        }}
      >
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Duplicar envío</DialogTitle>
            <DialogDescription>
              Se crearán copias del envío{" "}
              <strong>
                #{duplicateTarget?.internalId}
                {duplicateTarget?.senderName ? ` — ${duplicateTarget.senderName}` : ""}
              </strong>{" "}
              en estado <em>en planificación</em>, sin móvil ni fechas.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <Label htmlFor="dup-count" className="mb-1.5 block">
              Cantidad de copias <span className="text-destructive">*</span>
            </Label>
            <Input
              id="dup-count"
              type="number"
              min={1}
              max={50}
              value={duplicateCount}
              onChange={(e) => setDuplicateCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
              className="w-28"
              autoFocus
            />
            {duplicateCount > 1 && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                Se crearán {duplicateCount} envíos idénticos.
              </p>
            )}
          </div>

          <DialogFooter>
            <DialogClose>
              <Button variant="outline" onClick={() => setDuplicateTarget(null)}>
                Cancelar
              </Button>
            </DialogClose>
            <Button
              disabled={isDuplicating}
              onClick={() => duplicateTarget && duplicateMut.mutate({ id: duplicateTarget.id, count: duplicateCount })}
            >
              {isDuplicating
                ? "Creando..."
                : duplicateCount > 1
                  ? `Crear ${duplicateCount} copias`
                  : "Crear copia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete dialog ── */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar envío</DialogTitle>
            <DialogDescription>
              ¿Eliminás el envío{" "}
              <strong>
                #{deleting?.internalId}
                {deleting?.senderName ? ` — ${deleting.senderName}` : ""}
              </strong>
              ? Esta acción es reversible.{" "}
              {(deleting?.tripCount ?? 0) > 0 && (
                <span className="text-destructive font-medium">
                  También se eliminarán los {deleting!.tripCount} viaje{deleting!.tripCount !== 1 ? "s" : ""} asociado{deleting!.tripCount !== 1 ? "s" : ""}.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button variant="outline" onClick={() => setDeleting(null)}>
                Cancelar
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={() => deleting && deleteMut.mutate(deleting.id)}
            >
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Export Preview Modal ── */}
      <Dialog open={!!shipmentExportPreview} onOpenChange={(open) => { if (!open) setShipmentExportPreview(null) }}>
        <DialogContent className="max-w-5xl w-full flex flex-col" style={{ maxHeight: "85vh" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DownloadIcon className="size-4" />
              Preview de exportación
            </DialogTitle>
            <DialogDescription className="font-mono text-xs truncate">
              {shipmentExportPreview?.filename}
            </DialogDescription>
          </DialogHeader>
          {shipmentExportPreview ? (() => {
            const lines = shipmentExportPreview.csvText.trim().split("\n")
            const MISSING_SENTINEL = "\x00MISSING\x00"
            const isMissingCell    = (cell: string) => cell.startsWith(MISSING_SENTINEL)
            const displayCell      = (cell: string) => isMissingCell(cell) ? cell.slice(MISSING_SENTINEL.length) : cell
            const isProblematicRow = (row: string[]) => row.some(isMissingCell)
            const HEADER_LINES = 3
            const headers  = parseCSVRow(lines[HEADER_LINES - 1] ?? "")
            const xlsHeaderRows = [headers]
            const metaRows = lines.slice(0, HEADER_LINES - 1).map(parseCSVRow)
            const allRows  = lines.slice(HEADER_LINES).map(parseCSVRow)
            const okRows   = allRows.filter(r => !isProblematicRow(r))
            const badCount = allRows.length - okRows.length
            const escape   = (c: string) => escapeCSVCell(displayCell(c))
            const cleanCsvText = [
              ...lines.slice(0, HEADER_LINES),
              ...okRows.map(r => r.map(escape).join(",")),
            ].join("\n")
            return (
              <>
                <div className="flex items-center gap-2 pb-2 border-b">
                  <Button size="sm" className="gap-2" onClick={() => handleDownloadShipmentPreview(cleanCsvText)}>
                    <DownloadIcon className="size-4" /> Descargar CSV
                  </Button>
                  <Button size="sm" variant="outline" className="gap-2" onClick={() => handleDownloadShipmentXlsPreview(xlsHeaderRows, okRows.map(r => r.map(displayCell)))}>
                    <DownloadIcon className="size-4" /> Descargar XLS (97–2003)
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    {okRows.length} fila{okRows.length !== 1 ? "s" : ""}
                    {badCount > 0 && (
                      <span className="ml-2 text-amber-600 font-medium">
                        · {badCount} con problema (excluida{badCount !== 1 ? "s" : ""})
                      </span>
                    )}
                  </span>
                </div>
                <div className="overflow-auto flex-1 rounded border">
                  <table className="text-xs w-full border-collapse">
                    <thead className="sticky top-0 bg-muted z-10">
                      {metaRows.map((row, ri) => (
                        <tr key={`meta-${ri}`}>
                          {row.map((h, i) => (
                            <th key={i} className="border-b border-r px-2 py-1.5 text-left whitespace-nowrap text-muted-foreground/60 text-[10px] last:border-r-0">
                              {h}
                            </th>
                          ))}
                        </tr>
                      ))}
                      <tr>
                        {headers.map((h, i) => (
                          <th key={i} className="border-b border-r px-2 py-1.5 text-left font-semibold whitespace-nowrap text-muted-foreground last:border-r-0">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {allRows.map((row, ri) => {
                        const bad = isProblematicRow(row)
                        return (
                          <tr key={ri} className={bad ? "bg-amber-50" : ri % 2 === 0 ? "bg-background" : "bg-muted/30"}>
                            {row.map((cell, ci) => {
                              const cellMissing = isMissingCell(cell)
                              return (
                                <td key={ci} className={cn(
                                  "border-b border-r px-2 py-1 whitespace-nowrap last:border-r-0 font-mono",
                                  cellMissing ? "bg-red-100 text-red-700 font-semibold" : bad ? "text-amber-700" : "",
                                )}>
                                  {displayCell(cell)}
                                </td>
                              )
                            })}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )
          })() : null}
        </DialogContent>
      </Dialog>

      {/* ── Cancel Dialog ──────────────────────────────────────────────────── */}
      <Dialog open={!!cancelTarget} onOpenChange={open => { if (!open) { setCancelTarget(null); setCancelError("") } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancelar envío #{cancelTarget?.internalId}</DialogTitle>
            <DialogDescription>
              Esta acción cambia el estado a <strong>Cancelado</strong>. Podés revertirla editando el estado del envío.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="cancel-reason">Motivo <span className="text-destructive">*</span></Label>
              <Select value={cancelReason} onValueChange={(v) => setCancelReason(v ?? "")}>
                <SelectTrigger id="cancel-reason">
                  <SelectValue>
                    {cancelReason
                      ? ({ clima: "Clima", rotura: "Rotura", chofer: "Chofer", externo: "Externo", otro: "Otro" }[cancelReason] ?? cancelReason)
                      : <span className="text-muted-foreground">Seleccioná un motivo</span>}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="clima">Clima</SelectItem>
                  <SelectItem value="rotura">Rotura</SelectItem>
                  <SelectItem value="chofer">Chofer</SelectItem>
                  <SelectItem value="externo">Externo</SelectItem>
                  <SelectItem value="otro">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cancel-description">Descripción <span className="text-destructive">*</span></Label>
              <textarea
                id="cancel-description"
                placeholder="Describí el motivo de la cancelación..."
                value={cancelDescription}
                onChange={e => setCancelDescription(e.target.value)}
                rows={3}
                maxLength={500}
                required
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              />
            </div>
            {cancelError && <p className="text-sm text-destructive">{cancelError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelTarget(null); setCancelError("") }}>
              Volver
            </Button>
            <Button
              variant="destructive"
              disabled={!cancelReason || !cancelDescription.trim() || cancelMut.isPending}
              onClick={() => {
                if (!cancelTarget || !cancelReason || !cancelDescription.trim()) return
                cancelMut.mutate({ id: cancelTarget.id, reason: cancelReason, description: cancelDescription.trim() })
              }}
            >
              {cancelMut.isPending ? "Cancelando..." : "Confirmar cancelación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Confirm status change dialog ── */}
      <Dialog open={!!confirmDialog} onOpenChange={(open) => { if (!open) setConfirmDialog(null) }}>
        <DialogContent showCloseButton={false} className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{confirmDialog?.title}</DialogTitle>
            <DialogDescription>{confirmDialog?.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(null)}>Cancelar</Button>
            <Button
              onClick={async () => {
                const fn = confirmDialog?.onConfirm
                setConfirmDialog(null)
                await fn?.()
              }}
            >
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
